import {
  CalculatorOutlined,
  SaveOutlined,
  SendOutlined,
  CheckCircleOutlined,
  DownloadOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { saveBothWallsBuildUp } from "../../utils/wallsBuildUpSave";
import { RETAINING_WALLS_BUILD_UP_QUERY_KEYS } from "../../hooks/useRetainingWallsBuildUp";
import { GEO_TOBER_WALL_BUILD_UP_QUERY_KEYS } from "../../hooks/useWallSectionBuildUp";
import { forceSaveBothBoqEndpoints, getCurrentBoqVersion } from "../../utils/boqVersionUtils";
import { createBoqPayload, createMutationPayload, createVersionPayload } from "../../utils/boqPayloadBuilder";
import {
  Alert,
  Button,
  Card,
  Progress,
  Space,
  Typography,
  Modal,
  notification,
  message,
  Input,
  Switch,
} from "antd";
import React, { useState, useEffect, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useScrollToTop } from "../../hooks/useScrollToTop";
import { useTender } from "../../contexts/TenderContext";
import { useAutoSave } from "../../hooks/useAutoSave";
import { useGlobalSettings } from "../../hooks/useGlobalSettings";
import { useAuth } from "../../hooks/useAuth";
import { useUserRole } from "../../hooks/useUserRole";
import { useLocations } from "../../hooks/useLocations";
import { useItemLibrary } from "../../hooks/useItemLibrary";
import { useRoadsAndSewersTotalSync } from "../../hooks/useRoadsAndSewersTotalSync";
import { useOpportunities } from "../../hooks/useOpportunities";
import { opportunitiesApi } from "../../api/opportunities";
// import { useChangeDetectionContext } from "../../contexts/ChangeDetectionContext";
import {
  useBillOfQuantities,
  useCreateBillOfQuantities,
  useBoqLatestVersion,
  useBoqSelectedVersion,
  BOQ_QUERY_KEYS,
} from "../../hooks/useBillOfQuantities";
import { billOfQuantitiesApi } from "../../api/billOfQuantities";
import { getReviewButtonState } from "../../utils/reviewButtonHelper";
import { CalculationProgress } from "../../types/tender.types";
import { CalculationEngine } from "../../utils/CalculationEngine";
import { generateBOQExcelData, downloadAsExcel } from "../../utils/exportUtils";
import ExportOptionsModal, {
  ExportDetailOptions,
} from "../common/ExportOptionsModal";
import SendTenderReviewModal from "../common/SendTenderReviewModal";
import TextInput from "../common/TextInput";
import DeleteConfirmationModal from "../common/DeleteConfirmationModal";
import { useDynamicBillSummary } from "../../hooks/useDynamicBillSummary";
import { useConfigureBoqContext } from "@/contexts/ConfigureBoqContext";
import { CONFIGURE_BOQ_SWITCH_LABEL } from "@/config/dynamicSummaryConfig";
// Roads & Sewers-only serialization helpers — kept separate from
// dynamicBillSummarySerialization.ts (Groundworks' own file) so this page's
// Dynamic Bill Summary support can never change /groundworks/review's
// behaviour. `useDynamicBillSummary`/`DynamicBillSummaryPanel` above ARE the
// shared groundworks hook/component, reused here as-is (no groundworks file
// edited) — this page never passes `plotMatrixColumns` through (Roads &
// Sewers has no Plot Matrix), but does pass `enableRunMatrixMapping`, which
// surfaces the panel's parallel "Run Matrix Mapping" section instead — see
// dynamicBillSummaryRunMatrix.ts.
import {
  getRawRoadsAndSewersDynamicBillSummaryTabItems,
  flattenDynamicBillSummaryLineItems,
  deserializeRoadsAndSewersDynamicBillSummaryTabs,
  computeDynamicBillSummaryNodeAmount,
} from "../../utils/roadsAndSewersDynamicBillSummarySerialization";
import type { DynamicBillSummaryLineItem } from "../../utils/roadsAndSewersDynamicBillSummarySerialization";
// `computeDynamicBillSummaryNodeRate` isn't re-exported from the Roads &
// Sewers-only serialization file above (only the domain-agnostic node-tree
// helpers are) — pulled from its actual source, same as every other caller.
import { computeDynamicBillSummaryNodeRate, deserializeCutFillsExtraItems } from "../../utils/dynamicBillSummarySerialization";
import { BoQExporter } from "../../utils/boqExports";
import { BoQSection, BoQSummary, DynamicBillSummaryNode } from "../../types/tender.types";
import MainLayout from "../layout/MainLayout";
import TenderDetailPage from "./groundworks/TenderDetailPage";
import {
  clearTenderFromStorage,
  saveTenderToStorage,
} from "../../utils/storage";

import { rsSumAnalysis } from "../../mock/rs-sum-analysis";
import { groundworksSumAnalysis } from "../../mock/groudworks-sum-analysis";
import { MNJLOGOBASE64, COLORS } from "../../utils/constants";
import { createCoverSheet, createEndCover, CoverSheetData } from "../../utils/excelCoverSheet";
import { createNotesSheet } from "../../utils/notesSheetGenerator";
import {
  autoFitColumns,
  createWorksheetHeader,
  styleColumnHeaders,
  styleDataRow,
  styleCategoryHeaderRow,
  styleTotalRow,
  BOQ_PAGE_SETUP,
} from "../../utils/excelBoqSheetStyling";
import { computeRoadsAndSewersBoqSummary } from "../../utils/roadsAndSewersBoqSummaryComputer";
import { buildRoadsAndSewersSumAnalysisRows } from "../../utils/roadsAndSewersSumAnalysisRows";
import type { BoqSummaryLineItem } from "../../utils/groundworksBoqSummaryComputer";
import { rsRoads as roadsBillSummaryMock } from "../../mock/rs-roads";
import { rsFoulSewers as foulSewersBillSummaryMock } from "../../mock/rs-foul-sewers";
import { rsStormSewers as stormSewersBillSummaryMock } from "../../mock/rs-storm-sewers";
import { rsMainServices as mainServicesBillSummaryMock } from "../../mock/rs-main-services";
import { rsPreliminaryItems as preliminariesBillSummaryMock } from "../../mock/rs-preliminary-items";
import PrelimsBillSummary from "./roads-sewers/PrelimsBillSummary";
import FoulSewersBillSummary from "./roads-sewers/FoulSewersBillSummary";
import StormSewersBillSummary from "./roads-sewers/StormSewersBillSummary";
import RoadsBillSummary from "./roads-sewers/RoadsBillSummary";
import MainServicesBillSummary from "./roads-sewers/MainServicesBillSummary";
import SubstationBillSummary from "./roads-sewers/SubstationBillSummary";
import POSBillSummary from "./roads-sewers/POSBillSummary";
import CompoundBillSummary from "./roads-sewers/CompoundBillSummary";
import RsSumAnalysisBillSummary from "./roads-sewers/RsSumAnalysisBillSummary";
import RunMatrix from "./roads-sewers/RunMatrix";
import Title from "antd/es/typography/Title";

// Feature flags, explicit opt-in: only .env.development (DOGMA QA) ships these
// on, so the "Configure BOQ" switch and the Export Options button stay hidden
// on QA, UAT and production.
const showConfigureBoqSwitch = import.meta.env.VITE_SHOW_CONFIGURE_BOQ === "true";
const showRnsExports = import.meta.env.VITE_SHOW_RNS_EXPORTS === "true";

// Every fixed (non-dynamic) Bill Summary tab label this page can show — kept in
// sync with the "Bill Summary Sub-Tabs" button array below. Passed to
// useDynamicBillSummary as reservedLabels so a new/renamed dynamic tab can't
// take one of these names.
const FIXED_BILL_SUMMARY_TAB_LABELS = [
  "Sum Analysis",
  "Preliminary Items",
  "Foul Sewers",
  "Storm Sewers",
  "Roads",
  "Main Services",
  "Substation",
  "POS",
  "Compound",
];

/**
 * Excel worksheet names can't contain : \ / ? * [ ] and are capped at 31
 * characters, and every sheet in a workbook must have a unique name — none
 * of which a user-typed Dynamic Bill Summary tab label is guaranteed to
 * satisfy. `usedNames` tracks lower-cased names already claimed (seeded by
 * the caller with whatever fixed sheets already exist in the workbook) so a
 * collision gets a " (2)"-style suffix instead of ExcelJS throwing on
 * `addWorksheet`. Local near-duplicate of groundworksExcelExport.ts's own
 * (unexported) `sanitizeWorksheetName` — same contract, kept separate since
 * this page builds its workbook inline rather than through that file.
 */
const sanitizeWorksheetName = (label: string, usedNames: Set<string>): string => {
  const base = (label.replace(/[:\\/?*[\]]/g, "").trim() || "Bill Summary").slice(0, 31);
  let candidate = base;
  let suffixIndex = 2;
  while (usedNames.has(candidate.toLowerCase())) {
    const suffix = ` (${suffixIndex})`;
    candidate = `${base.slice(0, 31 - suffix.length)}${suffix}`;
    suffixIndex++;
  }
  usedNames.add(candidate.toLowerCase());
  return candidate;
};

const ReviewCalculate: React.FC = () => {
  useScrollToTop({ behavior: "smooth", delay: 100 });
  const navigate = useNavigate();
  const { state, updateField, dispatch, saveTender } = useTender();
  const { settings: globalSettings } = useGlobalSettings();
  const { user } = useAuth();
  const { role: userRole } = useUserRole();
  // "Configure BOQ" switch — same app-wide ConfigureBoqContext (App.tsx wraps
  // the whole router in ConfigureBoqProvider) /groundworks/review's own switch
  // reads/writes, persisted per-browser via localStorage. Gates the
  // Add Section/Add Item Actions column on every Bill Summary here
  // (useCanConfigureBoq(), read inside each roads-sewers/*BillSummary.tsx
  // component) and on this page's own Dynamic Bill Summary "+ Add Tab"
  // control. Without a visible switch on this page, a user who never visits
  // /groundworks/review would have no way to turn it on at all. Visible to
  // every user, but only where showConfigureBoqSwitch is on (DOGMA QA).
  const { configureBoq, setConfigureBoq } = useConfigureBoqContext();
  useAutoSave();
  const { data: locationsData } = useLocations();
  const { data: itemLibraryData, isLoading: isItemLibraryLoadingForExport, refetch: refetchItemLibrary } = useItemLibrary(state.region || "");
  const { data: opportunitiesData } = useOpportunities();
  const matchedOpportunityNo = state.opportunityNo;

  // Change detection context
  // const { registerPageChanges } = useChangeDetectionContext();

  // Check localStorage for historical version state
  const [isHistoricalVersion, setIsHistoricalVersion] = useState<boolean>(() => {
    return localStorage.getItem('isHistoricalVersion') === 'true';
  });

  const [isCalculating, setIsCalculating] = useState(false);
  const [hasCalculated, setHasCalculated] = useState(false);
  const [recalculateTrigger, setRecalculateTrigger] = useState(0);

  // Watch for changes in localStorage historical version state
  useEffect(() => {
    const checkHistoricalState = () => {
      const isHistorical = localStorage.getItem('isHistoricalVersion') === 'true';
      setIsHistoricalVersion(isHistorical);
    };

    // Check on mount and when storage changes
    checkHistoricalState();
    window.addEventListener('storage', checkHistoricalState);

    return () => {
      window.removeEventListener('storage', checkHistoricalState);
    };
  }, []);

  const [isSaving, setIsSaving] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionProgress, setSubmissionProgress] = useState<string>("");
  const [calculationProgress, setCalculationProgress] =
    useState<CalculationProgress | null>(null);
  const [_expandedAccordion, _setExpandedAccordion] = useState<string | false>(
    "summary"
  );
  const [submitModalVisible, setSubmitModalVisible] = useState(false);
  const [boqModalVisible, setBoqModalVisible] = useState(false);
  const [exportOptionsModalVisible, setExportOptionsModalVisible] =
    useState(false);
  const [boqData, setBoqData] = useState<any>(null);
  const [boqJson, setBoqJson] = useState<string>("");
  // State for tracking expanded rows in each section
  const [expandedRowKeys, setExpandedRowKeys] = useState<{
    [sectionKey: string]: React.Key[];
  }>({});
  const [isSentForReview, setIsSentForReview] = useState(false);
  const [isReviewModalVisible, setIsReviewModalVisible] = useState(false);
  const [isSendingForReview, setIsSendingForReview] = useState(false);
  // Frozen copy of reviewButtonState captured when the modal opens. Prevents
  // the modal from flashing empty content when the BOQ-status API update
  // causes reviewButtonState to recompute mid-submission.
  const [frozenReviewModal, setFrozenReviewModal] = useState<{
    title: string;
    message: string;
    buttonText: string;
    warningMessage?: string;
  } | null>(null);
  const [isInProgressModalVisible, setIsInProgressModalVisible] = useState(false);
  const [isSendingToInProgress, setIsSendingToInProgress] = useState(false);
  const [isWonModalVisible, setIsWonModalVisible] = useState(false);
  const [isSendingToWon, setIsSendingToWon] = useState(false);
  const [isManualTenderWarnModalVisible, setIsManualTenderWarnModalVisible] = useState(false);
  const [activeTab, setActiveTab] = useState<string>("sum-analysis");
  const [roadsBillSummaryExpandedKeys, setRoadsBillSummaryExpandedKeys] = useState<React.Key[]>([]);
  const [activeMainTab, setActiveMainTab] = useState<string>("boq");
  const [viewMode, setViewMode] = useState<"review" | "detail">("review");
  const [tenderApprovalStatus, setTenderApprovalStatus] = useState<
    "Approved" | "Pending review" | "Submitted"
  >("Pending review");

  // Derive BOQ status from cached version data (shared React Query cache)
  const { data: latestVersionData } = useBoqLatestVersion(state.projectName || undefined, { enabled: !isHistoricalVersion, oppNo: matchedOpportunityNo });
  const { data: selectedVersionData } = useBoqSelectedVersion(state.projectName || undefined, { enabled: isHistoricalVersion, oppNo: matchedOpportunityNo });
  const versionData = isHistoricalVersion ? selectedVersionData : latestVersionData;
  const apiBoqStatus = useMemo(() => {
    if (!versionData) return null;
    const d = versionData as any;
    return d?.boq?.boqStatus || d?.boq?.status || d?.snapshot?.boqStatus ||
      d?.snapshot?.status || d?.boqStatus || d?.status || null;
  }, [versionData]);

  // Calculate review button state based on current BOQ status and user role.
  // Mirrors GroundworksReview.tsx's reviewButtonState exactly: each role's
  // button also shows when status is "In-progress" (not just on the strict
  // prior-escalation status), so a user can jump straight to their own
  // action button as soon as the tender is In-progress.
  const reviewButtonState = useMemo(() => {
    // Prioritize apiBoqStatus (live API value) over state.boqStatus (may be stale context).
    const currentBoqStatus = apiBoqStatus || state.boqStatus || "In-progress";

    const statusLower = (currentBoqStatus || "").toLowerCase().trim();
    const roleLower = (userRole || "").toLowerCase().trim();
    const isInProgress = statusLower === "in-progress";

    // 1. Estimator role → show "Submit for Review Senior Estimator" button ONLY if status is "In-progress"
    if (roleLower === "estimator") {
      if (isInProgress) {
        return {
          buttonText: "Submit for Review Senior Estimator",
          nextStatus: "Under Review - Senior Estimator",
          isDisabled: false,
          modalTitle: "Submit for Review",
          modalMessage:
            "Do you want to submit this tender to the Senior Estimator for review?",
          shouldShow: true,
        };
      }

      // Hide button for all "Under Review" statuses
      return {
        buttonText: "",
        nextStatus: "",
        isDisabled: true,
        modalTitle: "",
        modalMessage: "",
        shouldShow: false,
      };
    }

    // 2. Senior Estimator role → show "Submit for Review Commercial Director" button
    if (roleLower === "senior estimator") {
      const isSeniorEstimatorReview =
        statusLower === "under review - senior estimator" ||
        statusLower === "under review-senior estimator" ||
        statusLower === "under review senior estimator" ||
        (statusLower.includes("senior estimator") &&
          statusLower.includes("review"));

      // Show button if status is "Under Review - Senior Estimator" OR "In-progress"
      if (isSeniorEstimatorReview || isInProgress) {
        return {
          buttonText: "Submit for Review Commercial Director",
          nextStatus: "Under Review Commercial Director",
          isDisabled: false,
          modalTitle: "Submit for Review",
          modalMessage:
            "Do you want to submit this tender to the Commercial Director for review?",
          shouldShow: true,
        };
      }

      // Hide button for other "Under Review" statuses (e.g., Commercial Director review, Client review)
      return {
        buttonText: "",
        nextStatus: "",
        isDisabled: true,
        modalTitle: "",
        modalMessage: "",
        shouldShow: false,
      };
    }

    // 3. Commercial Director role → show "Submit for Client Review" button
    if (roleLower === "commercial director") {
      const isCommercialDirectorReview =
        statusLower === "under review commercial director" ||
        statusLower === "under review - commercial director" ||
        (statusLower.includes("commercial director") &&
          statusLower.includes("review"));

      // Show button if status is "Under Review Commercial Director" OR "In-progress"
      if (isCommercialDirectorReview || isInProgress) {
        return {
          buttonText: "Submit for Client Review",
          nextStatus: "Under Client Review",
          isDisabled: false,
          modalTitle: "Submit for Client Review",
          modalMessage: "Do you want to submit this tender for client review?",
          shouldShow: true,
        };
      }

      // Hide button for other statuses
      return {
        buttonText: "",
        nextStatus: "",
        isDisabled: true,
        modalTitle: "",
        modalMessage: "",
        shouldShow: false,
      };
    }

    // Fallback to original logic for other roles
    return getReviewButtonState(currentBoqStatus, userRole);
  }, [apiBoqStatus, state.boqStatus, userRole, tenderApprovalStatus]);

  // Persist OH&P and preliminaries display preferences
  const [includePreliminariesDisplay, setIncludePreliminariesDisplay] =
    useState(() => {
      const saved = localStorage.getItem(
        "roadsAndSewers_includePreliminariesDisplay"
      );
      return saved ? JSON.parse(saved) : false;
    });

  const [includeOhpDisplay, setIncludeOhpDisplay] = useState(() => {
    const saved = localStorage.getItem("roadsAndSewers_includeOhpDisplay");
    return saved ? JSON.parse(saved) : false;
  });

  const [includeDiscountDisplay, setIncludeDiscountDisplay] = useState(() => {
    const saved = localStorage.getItem("roadsAndSewers_includeDiscountDisplay");
    return saved ? JSON.parse(saved) : false;
  });

  // Auto-select OH&P, Preliminaries, and Discount when global settings have values.
  // Must use the "Persistent" setters (not the plain useState setters) so
  // localStorage is updated too — every individual R&S Bill Summary tab
  // (Foul/Storm/Roads/Main Services/Substation/POS/Compound/Prelims) reads
  // its own markup factor straight from localStorage via
  // useRoadsAndSewersMarkupFactor (they aren't handed includeOhpDisplay/
  // includePreliminariesDisplay as props the way RsSumAnalysisBillSummary is).
  // Using the plain setters here left localStorage stale on a fresh
  // percentage entry — this component's own checkbox showed checked, Sum
  // Analysis (which reads the live prop) marked up correctly, but every
  // other tab kept reading the old, un-persisted localStorage value and
  // silently stayed un-marked-up.
  useEffect(() => {
    // If preliminaries has a value > 0, auto-check it
    if (globalSettings.roadsAndSewers.preliminaries > 0) {
      setIncludePreliminariesDisplayPersistent(true);
    }

    // If OH&P has a value > 0, auto-check it
    if (globalSettings.roadsAndSewers.overheadAndProfit > 0) {
      setIncludeOhpDisplayPersistent(true);
    }

    // If Discount has a value > 0, auto-check it
    if (globalSettings.roadsAndSewers.discount > 0) {
      setIncludeDiscountDisplayPersistent(true);
    }
  }, [
    globalSettings.roadsAndSewers.preliminaries,
    globalSettings.roadsAndSewers.overheadAndProfit,
    globalSettings.roadsAndSewers.discount,
  ]);

  // Helper functions to persist checkbox states
  const setIncludePreliminariesDisplayPersistent = (value: boolean) => {
    setIncludePreliminariesDisplay(value);
    localStorage.setItem(
      "roadsAndSewers_includePreliminariesDisplay",
      JSON.stringify(value)
    );
  };

  const setIncludeOhpDisplayPersistent = (value: boolean) => {
    setIncludeOhpDisplay(value);
    localStorage.setItem(
      "roadsAndSewers_includeOhpDisplay",
      JSON.stringify(value)
    );
  };

  const setIncludeDiscountDisplayPersistent = (value: boolean) => {
    setIncludeDiscountDisplay(value);
    localStorage.setItem(
      "roadsAndSewers_includeDiscountDisplay",
      JSON.stringify(value)
    );
    setRecalculateTrigger((prev) => prev + 1);
    // Register change for change detection
    // registerPageChanges("roads-sewers-review", true, 1);
  };

  // Live per-domain totals — the exact same hook/formula RsSumAnalysisBillSummary.tsx
  // uses for the on-screen Sum Analysis tab — called here too so the Excel export
  // (handleExportRoadsSewersToExcel) can build its Sum Analysis sheet from the
  // identical numbers regardless of which bill-summary sub-tab is active when the
  // user clicks Export. Idempotent alongside the other mounted instances of this
  // hook (MainLayout.tsx, RsSumAnalysisBillSummary.tsx) — the write-back effect
  // inside it no-ops once `roadsAndSewersTotals.grandTotal` already matches.
  const rsExportTotals = useRoadsAndSewersTotalSync(
    itemLibraryData,
    isItemLibraryLoadingForExport,
    {
      includeOhpDisplay,
      includePreliminariesDisplay,
      includeDiscountDisplay,
      recalculateTrigger,
    }
  );

  // Bill Summary Sub-Tabs — hide a fixed domain tab when it has no records,
  // same convention GroundworksReview.tsx's buildBillSummaryTabs() follows
  // (its hasTableData/hasExtraItemsData gate). Roads & Sewers has no single
  // per-domain state shape to inspect directly (each domain's own row-building
  // util differs), so "has records" here reads the same rsExportTotals figures
  // Sum Analysis/Export Options already treat as the source of truth for
  // whether a domain has anything in it — main total plus that domain's own
  // extra items, folding in each build-up sub-tab that lives under Foul Sewers
  // (Pump Station, Rising Main) / Storm Sewers (Ponds & Swales, Cellular/
  // Weholite/Conc Attenuation, Box Culverts) since those render inside the
  // parent tab rather than as separate buttons. Sum Analysis always shows,
  // matching Groundworks' `alwaysShow`.
  const billSummaryTabs = useMemo(() => {
    const tabs: { key: string; label: string; alwaysShow?: boolean; hasData?: boolean }[] = [
      { key: "sum-analysis", label: "Sum Analysis", alwaysShow: true },
      {
        key: "preliminary-items",
        label: "Preliminary Items",
        hasData: rsExportTotals.prelimsTotal + rsExportTotals.prelimsExtraItemsTotal > 0,
      },
      {
        key: "foul-sewers",
        label: "Foul Sewers",
        hasData:
          rsExportTotals.foulTotal + rsExportTotals.foulSewersExtraItemsTotal +
            rsExportTotals.pumpStationTotal + rsExportTotals.pumpStationExtraItemsTotal +
            rsExportTotals.risingMainTotal + rsExportTotals.risingMainExtraItemsTotal >
          0,
      },
      {
        key: "storm-sewers",
        label: "Storm Sewers",
        hasData:
          rsExportTotals.stormTotal + rsExportTotals.stormSewersExtraItemsTotal +
            rsExportTotals.pondsSwalesTotal + rsExportTotals.pondsSwalesExtraItemsTotal +
            rsExportTotals.cellularAttenuationTotal + rsExportTotals.cellularAttenuationExtraItemsTotal +
            rsExportTotals.weholiteAttenuationTotal + rsExportTotals.weholiteAttenuationExtraItemsTotal +
            rsExportTotals.concAttenuationTotal + rsExportTotals.concAttenuationExtraItemsTotal +
            rsExportTotals.boxCulvertsTotal + rsExportTotals.boxCulvertsExtraItemsTotal >
          0,
      },
      {
        key: "roads",
        label: "Roads",
        hasData: rsExportTotals.roadsTotal + rsExportTotals.roadsExtraItemsTotal > 0,
      },
      {
        key: "main-services",
        label: "Main Services",
        hasData: rsExportTotals.mainServicesTotal + rsExportTotals.mainServicesExtraItemsTotal > 0,
      },
      {
        key: "substation",
        label: "Substation",
        hasData: rsExportTotals.substationTotal + rsExportTotals.substationExtraItemsTotal > 0,
      },
      {
        key: "pos",
        label: "POS",
        hasData: rsExportTotals.posTotal + rsExportTotals.posExtraItemsTotal > 0,
      },
      {
        key: "compound",
        label: "Compound",
        hasData: rsExportTotals.compoundTotal + rsExportTotals.compoundExtraItemsTotal > 0,
      },
    ];
    return tabs.filter((tab) => tab.alwaysShow || tab.hasData);
  }, [rsExportTotals]);

  // Dynamic Bill Summary — user-defined tabs added via the "+" control next to
  // the "Bill of Quantities Summary" tab bar (Roads & Sewers counterpart of
  // GroundworksReview's own "+ Add Tab" feature). All add/rename/delete state
  // and the ready-to-render tab descriptors come from this one reusable hook.
  // No `plotMatrixColumns`/`hasGarages` are passed through — Roads & Sewers has
  // no Plot Matrix (it has Run Matrix instead, a different per-section concept).
  // `enableRunMatrixMapping: true` surfaces DynamicBillSummaryPanel's parallel
  // "Run Matrix Mapping" section (Section/Column, both static catalogs — see
  // dynamicBillSummaryRunMatrix.ts) so an item added here can fold into the
  // Run Matrix tab the same way a groundworks item folds into Plot Matrix.
  const apiRoadsAndSewersDynamicBillSummaryLineItemsByTabId = useMemo(() => {
    const out = new Map<string, Map<string, DynamicBillSummaryLineItem>>();
    if (!versionData) return out;
    const d = versionData as any;
    const summary =
      d?.boq?.billOfQuantities?.[0]?.roadAndSewers?.summary ||
      d?.billOfQuantities?.[0]?.roadAndSewers?.summary ||
      d?.snapshot?.billOfQuantities?.[0]?.roadAndSewers?.summary ||
      d?.data?.billOfQuantities?.[0]?.roadAndSewers?.summary;
    const rawByTabKey = getRawRoadsAndSewersDynamicBillSummaryTabItems(summary);
    Object.entries(rawByTabKey).forEach(([tabKey, items]) => {
      out.set(tabKey, flattenDynamicBillSummaryLineItems(items));
    });
    return out;
  }, [versionData]);
  // OH&P/Preliminaries markup — same formula RsSumAnalysisBillSummary.tsx's own
  // Sum Analysis rows apply (via useRoadsAndSewersTotalSync), threaded through
  // to the Dynamic Bill Summary tabs so a dynamic item is marked up exactly
  // like a fixed one, gated by the same display-toggle checkboxes.
  const dynamicBillSummaryMarkupFactor =
    (1 + (includeOhpDisplay ? globalSettings.roadsAndSewers.overheadAndProfit : 0) / 100) *
    (1 + (includePreliminariesDisplay ? globalSettings.roadsAndSewers.preliminaries : 0) / 100);
  const dynamicBillSummary = useDynamicBillSummary({
    tabs: state.roadsAndSewersDynamicBillSummaryTabs,
    onTabsChange: (updated) => updateField("roadsAndSewersDynamicBillSummaryTabs", updated),
    isViewMode: state.isViewMode,
    itemLibraryData,
    onTabCreated: (id) => setActiveTab(id),
    enableRunMatrixMapping: true,
    apiLineItemsByTabId: apiRoadsAndSewersDynamicBillSummaryLineItemsByTabId,
    markupFactor: dynamicBillSummaryMarkupFactor,
    reservedLabels: FIXED_BILL_SUMMARY_TAB_LABELS,
  });
  // Tab deletion state — DeleteConfirmationModal (below), matching the app's
  // shared delete-confirm convention, instead of a Popconfirm.
  const [deleteRsTabId, setDeleteRsTabId] = useState<string | null>(null);
  const handleDeleteRsDynamicTab = (id: string) => {
    // Always switch to whatever will be the LAST remaining tab after this
    // delete — computed from the current dynamic tab list (still includes
    // `id`) filtered down, since dynamicBillSummary.tabs is read before the
    // hook's own state update from handleDeleteTab below has landed.
    const remainingDynamicTabs = dynamicBillSummary.tabs.filter((t) => t.id !== id);
    const lastTab = remainingDynamicTabs[remainingDynamicTabs.length - 1];
    setActiveTab(lastTab ? lastTab.id : "sum-analysis");
    dynamicBillSummary.handleDeleteTab(id);
  };

  // Roads & Sewers Dynamic Bill Summary tabs — each persisted as its own key
  // under billOfQuantities[0].roadAndSewers.summary (e.g. a tab named "Others"
  // is summary.others), same shape every fixed summary domain key uses. See
  // boqPayloadBuilder.ts / boqVersionUtils.ts for the write side and
  // dynamicBillSummarySerialization.ts for the shared key scheme.
  const apiRoadsAndSewersDynamicBillSummaryTabs = useMemo(() => {
    if (!versionData) return undefined;
    const d = versionData as any;
    const summary =
      d?.boq?.billOfQuantities?.[0]?.roadAndSewers?.summary ||
      d?.billOfQuantities?.[0]?.roadAndSewers?.summary ||
      d?.snapshot?.billOfQuantities?.[0]?.roadAndSewers?.summary ||
      d?.data?.billOfQuantities?.[0]?.roadAndSewers?.summary;
    const tabs = deserializeRoadsAndSewersDynamicBillSummaryTabs(summary);
    return tabs.length > 0 ? tabs : undefined;
  }, [versionData]);

  // Hydrate once from the API into state — only when state doesn't already
  // carry the field, so this never fights with in-progress edits/autosave.
  useEffect(() => {
    if (apiRoadsAndSewersDynamicBillSummaryTabs && !state.roadsAndSewersDynamicBillSummaryTabs) {
      updateField("roadsAndSewersDynamicBillSummaryTabs", apiRoadsAndSewersDynamicBillSummaryTabs);
    }
  }, [apiRoadsAndSewersDynamicBillSummaryTabs]);

  // Each fixed Bill Summary tab's own ad-hoc "Add Item"/"Add Section" extra
  // items (useBillSummaryExtraItems.tsx) — same "field the reload path can't
  // otherwise see" situation as roadsAndSewersDynamicBillSummaryTabs above,
  // and the exact counterpart of GroundworksReview.tsx's cutFillsExtraItems/
  // foundationsExtraItems hydration. Each `*ExtraItems` bucket lives outside
  // the computed `summary` tree — only its FLATTENED form is persisted, folded
  // into the domain's own summary array by boqPayloadBuilder.ts/
  // boqVersionUtils.ts (`flattenCutFillsExtraItemsToBoqLines`) — so without
  // this, a page reload showed the saved BOQ's real quantities but silently
  // lost every ad-hoc extra row, even though the API genuinely had them.
  // `deserializeCutFillsExtraItems` is domain-agnostic (same helper
  // Groundworks uses) and reconstructs the bucket map from the persisted
  // `summary.<domain>` array via each line's `fieldName`/`category`.
  const roadsAndSewersSummaryForExtraItems = useMemo(() => {
    if (!versionData) return undefined;
    const d = versionData as any;
    return (
      d?.boq?.billOfQuantities?.[0]?.roadAndSewers?.summary ||
      d?.billOfQuantities?.[0]?.roadAndSewers?.summary ||
      d?.snapshot?.billOfQuantities?.[0]?.roadAndSewers?.summary ||
      d?.data?.billOfQuantities?.[0]?.roadAndSewers?.summary
    );
  }, [versionData]);

  const apiPrelimsExtraItems = useMemo(
    () => deserializeCutFillsExtraItems(roadsAndSewersSummaryForExtraItems?.prelims),
    [roadsAndSewersSummaryForExtraItems]
  );
  useEffect(() => {
    if (apiPrelimsExtraItems && !state.prelimsExtraItems) updateField("prelimsExtraItems", apiPrelimsExtraItems);
  }, [apiPrelimsExtraItems]);

  const apiFoulSewersExtraItems = useMemo(
    () => deserializeCutFillsExtraItems(roadsAndSewersSummaryForExtraItems?.foulSewers),
    [roadsAndSewersSummaryForExtraItems]
  );
  useEffect(() => {
    if (apiFoulSewersExtraItems && !state.foulSewersExtraItems) updateField("foulSewersExtraItems", apiFoulSewersExtraItems);
  }, [apiFoulSewersExtraItems]);

  const apiPumpStationExtraItems = useMemo(
    () => deserializeCutFillsExtraItems(roadsAndSewersSummaryForExtraItems?.pumpStation),
    [roadsAndSewersSummaryForExtraItems]
  );
  useEffect(() => {
    if (apiPumpStationExtraItems && !state.pumpStationExtraItems) updateField("pumpStationExtraItems", apiPumpStationExtraItems);
  }, [apiPumpStationExtraItems]);

  const apiRisingMainExtraItems = useMemo(
    () => deserializeCutFillsExtraItems(roadsAndSewersSummaryForExtraItems?.risingMain),
    [roadsAndSewersSummaryForExtraItems]
  );
  useEffect(() => {
    if (apiRisingMainExtraItems && !state.risingMainExtraItems) updateField("risingMainExtraItems", apiRisingMainExtraItems);
  }, [apiRisingMainExtraItems]);

  const apiStormSewersExtraItems = useMemo(
    () => deserializeCutFillsExtraItems(roadsAndSewersSummaryForExtraItems?.stormSewers),
    [roadsAndSewersSummaryForExtraItems]
  );
  useEffect(() => {
    if (apiStormSewersExtraItems && !state.stormSewersExtraItems) updateField("stormSewersExtraItems", apiStormSewersExtraItems);
  }, [apiStormSewersExtraItems]);

  const apiPondsSwalesExtraItems = useMemo(
    () => deserializeCutFillsExtraItems(roadsAndSewersSummaryForExtraItems?.pondsSwales),
    [roadsAndSewersSummaryForExtraItems]
  );
  useEffect(() => {
    if (apiPondsSwalesExtraItems && !state.pondsSwalesExtraItems) updateField("pondsSwalesExtraItems", apiPondsSwalesExtraItems);
  }, [apiPondsSwalesExtraItems]);

  const apiCellularAttenuationExtraItems = useMemo(
    () => deserializeCutFillsExtraItems(roadsAndSewersSummaryForExtraItems?.cellularAttenuation),
    [roadsAndSewersSummaryForExtraItems]
  );
  useEffect(() => {
    if (apiCellularAttenuationExtraItems && !state.cellularAttenuationExtraItems) updateField("cellularAttenuationExtraItems", apiCellularAttenuationExtraItems);
  }, [apiCellularAttenuationExtraItems]);

  const apiWeholiteAttenuationExtraItems = useMemo(
    () => deserializeCutFillsExtraItems(roadsAndSewersSummaryForExtraItems?.weholiteAttenuation),
    [roadsAndSewersSummaryForExtraItems]
  );
  useEffect(() => {
    if (apiWeholiteAttenuationExtraItems && !state.weholiteAttenuationExtraItems) updateField("weholiteAttenuationExtraItems", apiWeholiteAttenuationExtraItems);
  }, [apiWeholiteAttenuationExtraItems]);

  const apiConcAttenuationExtraItems = useMemo(
    () => deserializeCutFillsExtraItems(roadsAndSewersSummaryForExtraItems?.concAttenuation),
    [roadsAndSewersSummaryForExtraItems]
  );
  useEffect(() => {
    if (apiConcAttenuationExtraItems && !state.concAttenuationExtraItems) updateField("concAttenuationExtraItems", apiConcAttenuationExtraItems);
  }, [apiConcAttenuationExtraItems]);

  const apiBoxCulvertsExtraItems = useMemo(
    () => deserializeCutFillsExtraItems(roadsAndSewersSummaryForExtraItems?.boxCulverts),
    [roadsAndSewersSummaryForExtraItems]
  );
  useEffect(() => {
    if (apiBoxCulvertsExtraItems && !state.boxCulvertsExtraItems) updateField("boxCulvertsExtraItems", apiBoxCulvertsExtraItems);
  }, [apiBoxCulvertsExtraItems]);

  const apiRoadsExtraItems = useMemo(
    () => deserializeCutFillsExtraItems(roadsAndSewersSummaryForExtraItems?.roads),
    [roadsAndSewersSummaryForExtraItems]
  );
  useEffect(() => {
    if (apiRoadsExtraItems && !state.roadsExtraItems) updateField("roadsExtraItems", apiRoadsExtraItems);
  }, [apiRoadsExtraItems]);

  const apiMainServicesExtraItems = useMemo(
    () => deserializeCutFillsExtraItems(roadsAndSewersSummaryForExtraItems?.mainServices),
    [roadsAndSewersSummaryForExtraItems]
  );
  useEffect(() => {
    if (apiMainServicesExtraItems && !state.mainServicesExtraItems) updateField("mainServicesExtraItems", apiMainServicesExtraItems);
  }, [apiMainServicesExtraItems]);

  const apiSubstationExtraItems = useMemo(
    () => deserializeCutFillsExtraItems(roadsAndSewersSummaryForExtraItems?.substation),
    [roadsAndSewersSummaryForExtraItems]
  );
  useEffect(() => {
    if (apiSubstationExtraItems && !state.substationExtraItems) updateField("substationExtraItems", apiSubstationExtraItems);
  }, [apiSubstationExtraItems]);

  const apiPosExtraItems = useMemo(
    () => deserializeCutFillsExtraItems(roadsAndSewersSummaryForExtraItems?.pos),
    [roadsAndSewersSummaryForExtraItems]
  );
  useEffect(() => {
    if (apiPosExtraItems && !state.posExtraItems) updateField("posExtraItems", apiPosExtraItems);
  }, [apiPosExtraItems]);

  const apiCompoundExtraItems = useMemo(
    () => deserializeCutFillsExtraItems(roadsAndSewersSummaryForExtraItems?.compound),
    [roadsAndSewersSummaryForExtraItems]
  );
  useEffect(() => {
    if (apiCompoundExtraItems && !state.compoundExtraItems) updateField("compoundExtraItems", apiCompoundExtraItems);
  }, [apiCompoundExtraItems]);

  // API integration for Bill of Quantities
  const {
    data: apiBoqData,
    isLoading: isLoadingBoq,
    error: boqError,
  } = useBillOfQuantities({});

  // Create mutation for submitting BOQ data
  const createBoqMutation = useCreateBillOfQuantities();
  const queryClient = useQueryClient();

  console.log("data", apiBoqData);

  // Use API BOQ status for display (already fetched in useEffect above)
  const currentBoqStatusForDisplay =
    apiBoqStatus || state.boqStatus || "In-progress";


  // Comprehensive Excel Export with All Summary Sheets and Logo for Roads & Sewers.
  // OH&P/Preliminaries/Discount markup is sourced from `rsExportTotals`
  // (useRoadsAndSewersTotalSync, called at the top of this component with the
  // current includeOhpDisplay/includePreliminariesDisplay/includeDiscountDisplay
  // state) rather than parameters here, so the export always reflects whatever
  // the on-screen toggles are set to at click-time — no separate plumbing needed.
  const handleExportRoadsSewersToExcel = async (
    options?: ExportDetailOptions
  ) => {
    console.log("🚀 Starting Roads & Sewers ExcelJS export with logo...");

    try {
      // Import ExcelJS directly
      const ExcelJS = await import("exceljs");
      console.log("✅ ExcelJS imported successfully");

      // Create workbook
      const workbook = new ExcelJS.default.Workbook();
      console.log("✅ Workbook created");

      // Get project information from state
      const projectName = state.projectName || "Roads & Sewers Project";
      const clientName = state.client || "CLIENT NAME";
      const region = state.region || "REGION";
      const primaryColor = "003366"; // App's primary color (#003366) without # for Excel

      // Calculate tender total for cover sheet
      // Same live total the Sum Analysis sheet's TENDER TOTAL row uses below —
      // calculateRoadsAndSewersGrandTotal() derives from static mock data and
      // applies markup to the whole lump sum, which disagrees with the real
      // per-domain markup rules `useRoadsAndSewersTotalSync` implements.
      const roadsAndSewersTotal = rsExportTotals.finalTotal;

      // Find matching location from locations API based on region
      const matchedLocation = locationsData?.value.find(
        (location) =>
          state.region &&
          (location.code.toLowerCase().includes(state.region.toLowerCase()) ||
            state.region.toLowerCase().includes(location.code.toLowerCase()))
      );

      // Create cover sheet data
      const coverSheetData: CoverSheetData = {
        projectName,
        clientName,
        region,
        jobType: "Roads & Sewers",
        tenderTotal: roadsAndSewersTotal,
        dateCreated: new Date(),
        description: `Complete roads and sewers bill of quantities for ${projectName}. This document includes detailed breakdowns for foul sewers, storm sewers, roads construction, and preliminary items with comprehensive calculations.`,
        siteAddress: state.siteAddress || undefined,
        locationData: matchedLocation
          ? {
              addressLine1: matchedLocation.addressLine1,
              addressLine2: matchedLocation.addressLine2,
              city: matchedLocation.city,
              postalCode: matchedLocation.postalCode,
              phoneNumber: matchedLocation.phoneNumber,
            }
          : undefined,
      };

      // Create the cover sheet (this will be the first sheet)
      createCoverSheet(workbook, coverSheetData);

      // Load logo the same way groundworks does — a base64 constant added
      // once, rather than a runtime fetch("/mnj-logo.jpg") that silently
      // degrades to "no logo" on a network hiccup.
      const logoImageId = workbook.addImage({
        base64: MNJLOGOBASE64,
        extension: "jpeg",
      });

      // Same source groundworks' export threads through as projectInfo.creationDate
      // (state.dateCreated, the tender dashboard's creation date) — previously this
      // export ignored it entirely and stamped every sheet with today's date.
      const creationDate =
        state.dateCreated instanceof Date
          ? state.dateCreated
          : new Date(state.dateCreated);

      // Helper to create the standard sheet header block (logo banner, title,
      // project details, generated date) — delegates to the exact same
      // createWorksheetHeader groundworksExcelExport.ts uses, so the two
      // exports can never visually drift apart again. numColumns/logoMergeColumns
      // mirror groundworks' own two call patterns: Sum Analysis passes an
      // explicit 2-column/2-column-merge + creationDate; every other sheet
      // passes only numColumns (creationDate/logoMergeColumns default, same
      // as groundworks' createBillSummarySheet/createDynamicBillSummarySheet).
      const createHeaders = (
        worksheet: any,
        sheetTitle: string,
        numColumns: number,
        isSumAnalysis = false
      ) =>
        isSumAnalysis
          ? createWorksheetHeader(
              worksheet,
              logoImageId,
              sheetTitle,
              projectName,
              clientName,
              region,
              primaryColor,
              numColumns,
              creationDate,
              numColumns
            )
          : createWorksheetHeader(
              worksheet,
              logoImageId,
              sheetTitle,
              projectName,
              clientName,
              region,
              primaryColor,
              numColumns
            );

      // Auto-fit column widths from actual content, matching groundworks'
      // createBillSummarySheet convention exactly (Description capped at 80,
      // other columns at 25, Amount guaranteed a minimum of 16).
      const autoFitBoqColumns = (worksheet: any, headers: string[]) => {
        const descColIndex = headers.indexOf("Description") + 1;
        const amountColIndex = headers.indexOf("Amount") + 1;
        const colMaxWidths: Record<number, number> = {};
        const colMinWidths: Record<number, number> = {};
        headers.forEach((_h, i) => {
          const colNum = i + 1;
          colMaxWidths[colNum] = colNum === descColIndex ? 80 : 25;
          if (colNum === amountColIndex) colMinWidths[colNum] = 16;
        });
        autoFitColumns(worksheet, {
          columnMaxWidths: colMaxWidths,
          columnMinWidths: colMinWidths,
          defaultMin: 6,
          padding: 2,
          minTotalWidth: 100,
        });
      };

      // Helper function to detect if an item is a category header
      const isCategoryHeader = (item: any) => {
        // Category headers typically have Item numbers like "1.0", "2.0", etc. and uppercase descriptions
        if (!item.Item || !item.Item.match(/^\d+\.\d+$/) || !item.Description) {
          return false;
        }

        const desc = item.Description.toUpperCase();

        // Check if it's all uppercase or contains category keywords
        return (
          item.Description === item.Description.toUpperCase() ||
          desc.includes("SEWERS") ||
          desc.includes("ROADS") ||
          desc.includes("SERVICES") ||
          desc.includes("PRELIMINARIES") ||
          desc.includes("CLEARANCE") ||
          desc.includes("EARTHWORKS") ||
          desc.includes("ADOPTED") ||
          desc.includes("FOUNDATIONS") ||
          desc.includes("EXTERNAL WORKS") ||
          // Pattern for main section headers (item numbers ending in .0)
          item.Item.endsWith(".0")
        );
      };

      // Helper function to detect if an item is a subcategory header
      const isSubcategoryHeader = (item: any) => {
        // Subcategory headers have Item numbers like "1.1", "1.2", etc. but not .0
        return (
          item.Item &&
          item.Item.match(/^\d+\.\d+$/) &&
          !item.Item.endsWith(".0") &&
          item.Description &&
          item.Description.trim() !== "" &&
          !isCategoryHeader(item)
        );
      };

      // Helper function to add spacing before category headers
      const addCategorySpacing = (
        worksheet: any,
        rowNum: number,
        colCount: number
      ) => {
        for (let col = 1; col <= colCount; col++) {
          const cell = worksheet.getCell(rowNum, col);
          cell.value = "";
        }
        worksheet.getRow(rowNum).height = 15;
        return rowNum + 1;
      };

      // Helper function to create column mapping based on options
      const createColumnMapping = (baseColumns: any) => {
        const result: any = {};

        if (baseColumns.Item !== undefined) {
          result["Item"] = baseColumns.Item;
        }

        if (!options || options.includeDescriptions) {
          result["Description"] = baseColumns.Description;
        }

        if (!options || options.includeQuantities) {
          if (baseColumns.Quantity !== undefined)
            result["Quantity"] = baseColumns.Quantity;
        }

        if (
          (!options || options.includeQuantities) &&
          baseColumns.Unit !== undefined
        ) {
          result["Unit"] = baseColumns.Unit;
        }

        if (!options || options.includeRates) {
          if (baseColumns.Rate !== undefined) result["Rate"] = baseColumns.Rate;
        }

        if (!options || options.includeTotals) {
          if (baseColumns.Amount !== undefined)
            result["Amount"] = baseColumns.Amount;
        }

        return result;
      };

      // Helper function to add section total row
      const addTotalRow = (
        worksheet: any,
        startRow: number,
        headers: string[],
        sectionTotal?: number
      ) => {
        const totalRowIndex = startRow;
        const totalRow = worksheet.getRow(totalRowIndex);

        // Add empty row before total for spacing
        const spacerRowIndex = totalRowIndex - 1;
        worksheet.getRow(spacerRowIndex).height = 10;

        // Find the amount column index
        const amountColIndex = headers.findIndex((h) => h === "Amount");
        const descriptionColIndex = headers.findIndex(
          (h) => h === "Description"
        );

        if (descriptionColIndex >= 0) {
          totalRow.getCell(descriptionColIndex + 1).value = "TOTAL";
        }

        if (amountColIndex >= 0 && sectionTotal !== undefined) {
          totalRow.getCell(amountColIndex + 1).value =
            fmtCurrencyForExport(sectionTotal);
        }

        // Whole-row gold "grand total" styling — matches groundworks marking
        // each domain sheet's own bottom-line total (this sheet has only one
        // overall total, no per-category subtotals, so it always plays that role).
        styleTotalRow(worksheet, totalRowIndex, headers.length, true);
      };

      // Function to add a sheet with proper formatting
      const addSheet = (
        sheetName: string,
        data: any[],
        sheetTitle: string,
        sectionTotal?: number
      ) => {
        if (!data || data.length === 0) {
          console.warn(`No data available for ${sheetName}`);
          return;
        }

        const worksheet = workbook.addWorksheet(sheetName);
        const isSumAnalysis = sheetName === "Sum Analysis";

        // Special handling for Sum Analysis - only Description and Amount columns
        let processedData;
        if (isSumAnalysis) {
          processedData = data.map((item) => ({
            Description: item.Description || "",
            Amount: item.Amount || "",
          }));
        } else {
          // Process data with column mapping for all other sheets
          processedData = data.map((item) => {
            const baseColumns = {
              Item: item.item || item.Item || "",
              Description: item.description || item.Description || "",
              Quantity:
                item.quantity || item.Quantity || item.qty || item.Qty || "",
              Unit: item.unit || item.Unit || "",
              Rate: item.rate || item.Rate || "",
              Amount:
                item.amount || item.Amount || item.total || item.Total || "",
            };

            return createColumnMapping(baseColumns);
          });
        }

        if (processedData.length === 0) return;

        const headers = Object.keys(processedData[0]);

        // Create headers with logo — Sum Analysis mirrors groundworks'
        // createSumAnalysisSheet exactly (2 columns, logo banner merged to
        // column B, explicit creation date).
        const startRow = createHeaders(worksheet, sheetTitle, headers.length, isSumAnalysis);

        styleColumnHeaders(worksheet, headers, startRow, primaryColor);

        // Add data rows with category formatting and consistent styling
        let currentRow = startRow + 1;
        let lastWasCategoryHeader = false;

        processedData.forEach((item, rowIndex) => {
          const originalItem = data[rowIndex]; // Get original data item for header detection

          // Check if this is a category or subcategory header
          const isMainCategory = isCategoryHeader(originalItem);
          const isSubCategory = isSubcategoryHeader(originalItem);
          // Sum Analysis has no Item-numbered rows, so isCategoryHeader never
          // fires for it — its own "grand total" row is detected by label
          // instead, matching groundworks' TENDER TOTAL gold-row treatment.
          const isGrandTotalRow =
            isSumAnalysis && (item as { Description?: string }).Description === "TENDER TOTAL";

          // Add spacing before main category headers (except the first one)
          if (isMainCategory && rowIndex > 0 && !lastWasCategoryHeader) {
            currentRow = addCategorySpacing(
              worksheet,
              currentRow,
              headers.length
            );
          }

          const dataRow = worksheet.getRow(currentRow);

          // Header rows (main/sub category) export upper-cased, matching the
          // on-screen Bill Summary header treatment.
          const descColKey = headers.indexOf("Description");
          Object.values(item).forEach((value: any, colIndex) => {
            dataRow.getCell(colIndex + 1).value =
              (isMainCategory || isSubCategory) &&
              colIndex === descColKey &&
              typeof value === "string"
                ? value.toUpperCase()
                : value;
          });

          // Apply appropriate styling based on item type
          if (isGrandTotalRow) {
            styleTotalRow(worksheet, currentRow, headers.length, true);
            lastWasCategoryHeader = false;
          } else if (isMainCategory) {
            styleCategoryHeaderRow(worksheet, currentRow, headers.length, true);
            lastWasCategoryHeader = true;
          } else if (isSubCategory) {
            styleCategoryHeaderRow(
              worksheet,
              currentRow,
              headers.length,
              false
            );
            lastWasCategoryHeader = false;
          } else {
            styleDataRow(worksheet, currentRow, headers.length);
            lastWasCategoryHeader = false;
          }

          currentRow++;
        });

        // Content-driven column widths, matching groundworks exactly.
        autoFitBoqColumns(worksheet, headers);
        worksheet.pageSetup = BOQ_PAGE_SETUP;

        // Add section total if provided and not the Sum Analysis sheet
        if (sectionTotal !== undefined && sheetName !== "Sum Analysis") {
          const lastDataRow = startRow + processedData.length;
          addTotalRow(worksheet, lastDataRow + 2, headers, sectionTotal);
        }

        // Freeze panes to keep headers visible (freeze after column headers)
        // worksheet.views = [{ state: "frozen", xSplit: 0, ySplit: startRow }];
      };

      // Add Sum Analysis Sheet first — built from the exact same live totals
      // (useRoadsAndSewersTotalSync, via rsExportTotals) RsSumAnalysisBillSummary.tsx
      // renders on screen, instead of markup math applied on top of static mock
      // data.
      const fmtCurrencyForExport = (v: number) =>
        `£${v.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      const weholiteSupplierNameForExport =
        state.weholiteAttenuation?.config?.supplierName ?? "Weholite";

      // Each domain's ad-hoc extra items fold straight into that domain's own
      // row — never a separate "(Extra Items)" row — same convention
      // RsSumAnalysisBillSummary.tsx's on-screen `sections` follows, so this
      // export ties to what's shown on screen. Shared with RunMatrix.tsx's
      // own export "Summary" sheet via `buildRoadsAndSewersSumAnalysisRows`
      // so the two can't independently drift on which domains/extra-items
      // buckets are included.
      const sumAnalysisRows = buildRoadsAndSewersSumAnalysisRows(rsExportTotals, weholiteSupplierNameForExport);

      const sumAnalysisData: { Description: string; Amount: string }[] = sumAnalysisRows.map((row) => ({
        Description: row.label,
        Amount: fmtCurrencyForExport(row.amount),
      }));
      if (rsExportTotals.discountAmount > 0) {
        sumAnalysisData.push({
          Description: `DISCOUNT (${globalSettings.roadsAndSewers.discount}%)`,
          Amount: `-${fmtCurrencyForExport(rsExportTotals.discountAmount)}`,
        });
      }
      sumAnalysisData.push({
        Description: "TENDER TOTAL",
        Amount: fmtCurrencyForExport(rsExportTotals.finalTotal),
      });

      if (sumAnalysisData.length > 0) {
        addSheet(
          "Sum Analysis",
          sumAnalysisData,
          "Roads & Sewers Sum Analysis"
        );

        // Notes sheet immediately after Sum Analysis, matching groundworks.
        await createNotesSheet(
          workbook,
          logoImageId,
          { projectName, clientName, region, creationDate },
          primaryColor
        );
      }

      // Add individual bill summary sheets from LIVE tender data (matching
      // GroundworksReview.tsx's export, which pulls every sheet from live state
      // rather than static mock arrays). computeRoadsAndSewersBoqSummary returns
      // the same per-domain BoqSummaryLineItem trees the BOQ save/submit payload
      // uses (boqPayloadBuilder.ts), giving every sheet real quantities/rates.
      const liveSummary = computeRoadsAndSewersBoqSummary(state, itemLibraryData);

      // Converts a Dynamic Bill Summary node tree (both the "+ Add Tab" tabs'
      // own nodes and every fixed Bill Summary's ad-hoc "Add Section"/"Add
      // Item" extra rows — both use the same DynamicBillSummaryNode shape)
      // into the BoqSummaryLineItem shape addLiveDomainSheet's `walk` already
      // knows how to render, so this export can reuse that one tree-walker
      // instead of a second bespoke writer (the approach
      // GroundworksReview.tsx's export takes with its own writeSection/
      // writeItem). Uses the same computeDynamicBillSummaryNodeAmount/
      // computeDynamicBillSummaryNodeRate the on-screen panel and Sum
      // Analysis use, so these figures can't drift from what's on screen.
      const dynamicNodeToBoqLineItem = (node: DynamicBillSummaryNode): BoqSummaryLineItem => {
        if (node.type === "section") {
          const children = node.children.map(dynamicNodeToBoqLineItem);
          return {
            key: node.id,
            fieldName: "dynamicBillSummary",
            description: node.label,
            quantity: 0,
            unit: "",
            rate: 0,
            amount: children.reduce((sum, c) => sum + (c.amount || 0), 0),
            category: "Dynamic Bill Summary",
            children,
          };
        }
        const isText = node.amountType === "text";
        return {
          key: node.id,
          fieldName: "dynamicBillSummary",
          description: node.description,
          quantity: !isText && node.qty ? Math.round(node.qty) : 0,
          unit: isText ? "" : node.unit || "",
          rate:
            !isText && node.rate !== undefined
              ? computeDynamicBillSummaryNodeRate(node.rate, dynamicBillSummaryMarkupFactor)
              : 0,
          amount: computeDynamicBillSummaryNodeAmount(node, dynamicBillSummaryMarkupFactor),
          category: "Dynamic Bill Summary",
          amountType: node.amountType,
          amountText: node.amountText,
        };
      };

      // Writes one worksheet from a live BoqSummaryLineItem tree — a parent node
      // (has `children`) renders as a bold section/subsection header (depth 0/1+),
      // a leaf node renders as a normal data row. Mirrors addSheet's column-option
      // gating (includeDescriptions/Quantities/Rates/Totals) and reuses the same
      // style helpers (createHeaders/styleColumnHeaders/styleCategoryHeaderRow/
      // styleDataRow/addCategorySpacing/addTotalRow) rather than duplicating them.
      //
      // Grafts each attach-point bucket (useBillSummaryExtraItems.tsx) onto the
      // tree node whose key matches it, in the SAME pass that drops zero-quantity
      // placeholder rows — grafting has to happen before pruning, not after, or a
      // section with no real quantity yet (e.g. "No manholes entered yet") gets
      // pruned away before its grafted extra item is attached, silently dropping
      // it. A grafted node's own children are never subject to the zero-quantity
      // prune (text-only "Included"/"Rate Only" extra items have quantity 0 by
      // design). This mirrors PrelimsBillSummary.tsx's on-screen `injectExtraRows`,
      // so an item added onto an existing predefined section nests under that
      // section in the export exactly as it does on screen, instead of landing as
      // a flat row at the end of the sheet (the previous, incorrect behaviour).
      // A bucket whose attach key matches no node in this domain's tree (the row
      // was removed, or — Prelims only — the key needs translating, see
      // resolvePrelimsAttachKeys below) falls back to a flat top-level row/section
      // at the end, same as "__root__" (brand-new sections a user adds).
      //
      // The `=== 0` zero-quantity check (not `<= 0`) is unchanged from before:
      // some domains (e.g. Foul/Storm's road-construction "Credits" rows) carry a
      // deliberately negative quantity that must stay visible.
      const buildPrunedGraftedTree = (
        nodes: BoqSummaryLineItem[],
        buckets: Record<string, DynamicBillSummaryNode[]>,
        consumedKeys: Set<string>,
        resolveAttachKeys: (nodeKey: string) => string[]
      ): BoqSummaryLineItem[] =>
        nodes.reduce<BoqSummaryLineItem[]>((acc, node) => {
          const isParent = !!(node.children && node.children.length > 0);
          const prunedChildren = isParent
            ? buildPrunedGraftedTree(node.children!, buckets, consumedKeys, resolveAttachKeys)
            : [];

          const matchedKey = resolveAttachKeys(node.key).find(
            (candidate) => candidate !== "__root__" && (buckets[candidate]?.length ?? 0) > 0
          );
          const graftedChildren = matchedKey ? buckets[matchedKey].map(dynamicNodeToBoqLineItem) : [];
          if (matchedKey) consumedKeys.add(matchedKey);

          if (!isParent && graftedChildren.length === 0) {
            if (node.quantity === 0) return acc;
            acc.push(node);
            return acc;
          }

          const mergedChildren = [...prunedChildren, ...graftedChildren];
          if (mergedChildren.length === 0) return acc;
          acc.push({
            ...node,
            children: mergedChildren,
            amount: mergedChildren.reduce((sum, c) => sum + (c.amount || 0), 0),
          });
          return acc;
        }, []);

      const addLiveDomainSheet = (
        sheetName: string,
        rawTree: BoqSummaryLineItem[],
        sheetTitle: string,
        // Ad-hoc "Add Section"/"Add Item" extra rows (useBillSummaryExtraItems.tsx),
        // keyed by attach point ("__root__" for a brand-new top-level section, or
        // an existing row's own `key` when grafted onto it) — see
        // buildPrunedGraftedTree above.
        extraBuckets: Record<string, DynamicBillSummaryNode[]> = {},
        // Whether this sheet's on-screen table has an equivalent "Item"/
        // "Reference" numbering column — PrelimsBillSummary/FoulSewersBillSummary/
        // StormSewersBillSummary/MainServicesBillSummary/SubstationBillSummary/
        // POSBillSummary/CompoundBillSummary all do, but FoulBuildUpBillSummary.tsx
        // (Pump Station, Rising Main), StormBuildUpBillSummary.tsx (Ponds & Swales,
        // Cellular/Weholite/Conc Attenuation, Box Culverts), and RoadsBillSummary.tsx
        // (in the `showRateAmount` mode this review page renders) all start
        // directly with Description on screen — the export previously added an
        // "Item" column to every sheet regardless, which those sheets' users never
        // see on screen.
        includeItemColumn: boolean = true,
        // Prelims-only: its BoqSummaryLineItem tree keys (`cat.id`,
        // `prelim-${cat.id}-${grp.id}`) don't match the UI's attach-point keys
        // (`cat-${cat.id}`, `grp-${cat.id}-${grp.id}`) even though both are built
        // from the same PRELIMS_MAPPING — see resolvePrelimsAttachKeys below.
        resolveAttachKeys: (nodeKey: string) => string[] = (key) => [key]
      ) => {
        const consumedKeys = new Set<string>();
        const graftedTree = buildPrunedGraftedTree(rawTree, extraBuckets, consumedKeys, resolveAttachKeys);
        const rootExtraNodes = extraBuckets["__root__"] ?? [];
        if (rootExtraNodes.length > 0) consumedKeys.add("__root__");
        // A bucket whose attach key matches no node (the row was removed, or —
        // Main Services only — the UI's single collapsed "MAIN SERVICE
        // TRENCHING" header has no matching single node in the export tree,
        // which keeps one section per utility group instead) wraps under a
        // synthetic "Additional Items" header instead of landing as bare
        // unlabelled top-level rows — same convention PrelimsBillSummary.tsx's
        // own on-screen `orphanedExtraRows` uses for the identical situation.
        const orphanedWrapperNodes: BoqSummaryLineItem[] = Object.keys(extraBuckets)
          .filter((key) => key !== "__root__" && !consumedKeys.has(key) && extraBuckets[key].length > 0)
          .map((key) => {
            const children = extraBuckets[key].map(dynamicNodeToBoqLineItem);
            return {
              key: `orphaned-${key}`,
              fieldName: "dynamicBillSummary",
              description: "Additional Items",
              quantity: 0,
              unit: "",
              rate: 0,
              amount: children.reduce((sum, c) => sum + (c.amount || 0), 0),
              category: "Dynamic Bill Summary",
              children,
            };
          });
        const tree = [
          ...graftedTree,
          ...rootExtraNodes.map(dynamicNodeToBoqLineItem),
          ...orphanedWrapperNodes,
        ];

        const sumAmount = (nodes: BoqSummaryLineItem[]): number =>
          nodes.reduce(
            (sum, n) =>
              sum + (n.children?.length ? sumAmount(n.children) : n.amount || 0),
            0
          );

        if (!tree || tree.length === 0) {
          console.warn(`No data available for ${sheetName}`);
          return;
        }

        const worksheet = workbook.addWorksheet(sheetName);

        const includeDescriptions = !options || options.includeDescriptions;
        const includeQuantities = !options || options.includeQuantities;
        const includeRates = !options || options.includeRates;
        const includeTotals = !options || options.includeTotals;

        const headers: string[] = includeItemColumn ? ["Item"] : [];
        if (includeDescriptions) headers.push("Description");
        if (includeQuantities) headers.push("Quantity", "Unit");
        if (includeRates) headers.push("Rate");
        if (includeTotals) headers.push("Amount");

        const startRow = createHeaders(worksheet, sheetTitle, headers.length);
        styleColumnHeaders(worksheet, headers, startRow, primaryColor);

        let currentRow = startRow + 1;

        const walk = (
          nodes: BoqSummaryLineItem[],
          depth: number,
          parentNum: string
        ) => {
          nodes.forEach((node, idx) => {
            // Generic N-level numbering ("1.0" at depth 0, "1.2" at depth 1,
            // "1.2.1" at depth 2, and so on) — a header passes its OWN number
            // down as the next level's parentNum (not the number it received),
            // so a third level (e.g. Prelims' category → group → item, or a
            // grafted extra item nested inside a group) numbers correctly
            // instead of the previous hardcoded two-level scheme, which left
            // the "Item" column blank for every depth-2-or-deeper row.
            const num = depth === 0 ? `${idx + 1}.0` : `${parentNum}.${idx + 1}`;
            const isHeader = !!(node.children && node.children.length > 0);

            if (isHeader && depth === 0 && idx > 0) {
              currentRow = addCategorySpacing(worksheet, currentRow, headers.length);
            }

            const row = worksheet.getRow(currentRow);
            headers.forEach((h, colIndex) => {
              let value = "";
              if (h === "Item") value = num;
              else if (h === "Description")
                value = isHeader ? (node.description ?? "").toUpperCase() : node.description;
              else if (!isHeader) {
                if (h === "Quantity") value = node.quantity ? String(node.quantity) : "";
                else if (h === "Unit") value = node.unit || "";
                else if (h === "Rate") value = node.rate ? node.rate.toFixed(2) : "";
                else if (h === "Amount")
                  value =
                    node.amountType === "text"
                      ? node.amountText || ""
                      : node.amount
                      ? fmtCurrencyForExport(node.amount)
                      : "";
              }
              row.getCell(colIndex + 1).value = value;
            });

            if (isHeader) {
              styleCategoryHeaderRow(worksheet, currentRow, headers.length, depth === 0);
            } else {
              styleDataRow(worksheet, currentRow, headers.length);
            }
            currentRow++;

            if (isHeader) {
              walk(node.children!, depth + 1, depth === 0 ? String(idx + 1) : num);
            }
          });
        };

        walk(tree, 0, "");

        // Content-driven column widths, matching groundworks exactly.
        autoFitBoqColumns(worksheet, headers);
        worksheet.pageSetup = BOQ_PAGE_SETUP;

        if (includeTotals) {
          addTotalRow(worksheet, currentRow + 1, headers, sumAmount(tree));
        }
      };

      // Prelims-only key translation: PrelimsBillSummary.tsx's on-screen table
      // attaches extra items using its own row keys (`cat-${cat.id}` for a
      // category header, `grp-${cat.id}-${grp.id}` for a group header — see
      // PrelimsBillSummary.tsx), but computeRoadsAndSewersBoqSummary's prelims
      // tree (roadsAndSewersBoqSummaryComputer.ts's computePrelimsSection) gives
      // those same two node types plain `cat.id` and `prelim-${cat.id}-${grp.id}`
      // instead — both builders iterate the same PRELIMS_MAPPING and agree on
      // every leaf item's key, only the two header keys diverge. Without this
      // translation, buildPrunedGraftedTree's key match never fires for Prelims
      // category/group attach points, which is exactly the bug this exists to
      // fix (extras added onto an existing predefined Prelims section fell to
      // the bottom of the sheet as "orphaned" instead of nesting under it).
      const resolvePrelimsAttachKeys = (nodeKey: string): string[] =>
        nodeKey.startsWith("prelim-")
          ? [nodeKey, `grp-${nodeKey.slice("prelim-".length)}`]
          : [nodeKey, `cat-${nodeKey}`];

      // Sheets are created in the same order the on-screen "Bill Summary
      // Sub-Tabs" bar shows them (billSummaryTabs above, then the Dynamic
      // Bill Summary "+ Add Tab" tabs last) rather than the previous
      // arbitrary order, so a sheet's position in the workbook always matches
      // its tab's position on screen.

      addLiveDomainSheet(
        "Preliminary Items",
        liveSummary.prelims,
        "Preliminary Items Bill Summary",
        { ...state.prelimsExtraItems },
        true,
        resolvePrelimsAttachKeys
      );

      // Foul Sewers, Pump Station, and Rising Main each get their OWN sheet —
      // per direct feedback that lumping Pump Station/Rising Main's rows into
      // the Foul Sewers sheet (even under a labelled section, an earlier
      // version of this fix) still buried them inside a much bigger table.
      // `FoulSewersBillSummary.tsx` shows the three as Segmented sub-tabs of
      // one page, not three separate top-level Bill Summary tabs — but the
      // export doesn't have to mirror that grouping, only make each sub-view
      // unambiguously present. Each sub-view's own extra-item bucket now
      // stays with its own sheet instead of being merged into one combined
      // bucket, since a merged bucket only mattered when all three shared one
      // tree — `buildPrunedGraftedTree` matches a bucket entry to a node by
      // `key`, and the three sub-views' row keys never collide with each
      // other, so splitting the buckets changes nothing about which item
      // grafts onto which row.
      addLiveDomainSheet(
        "Foul Sewers",
        liveSummary.foulSewers,
        "Foul Sewers Bill Summary",
        { ...state.foulSewersExtraItems }
      );

      addLiveDomainSheet(
        "Pump Station",
        liveSummary.pumpStation,
        "Pump Station Bill Summary",
        { ...state.pumpStationExtraItems },
        false
      );

      addLiveDomainSheet(
        "Rising Main",
        liveSummary.risingMain,
        "Rising Main Bill Summary",
        { ...state.risingMainExtraItems },
        false
      );

      // Storm Sewers and its 5 build-up sub-views (Ponds & Swales, Cellular/
      // Weholite/Conc Attenuation, Box Culverts) each get their OWN sheet —
      // same split as Foul Sewers/Pump Station/Rising Main above, and for the
      // same reason: StormSewersBillSummary.tsx shows all 6 as sub-tabs of
      // one page, but combining them into one "Storm Sewers" sheet buried 5
      // sub-views' rows as unlabelled siblings after Storm Sewers' own rows.
      // Each sub-view keeps only its own extra-items bucket, same as above.
      addLiveDomainSheet(
        "Storm Sewers",
        liveSummary.stormSewers,
        "Storm Sewers Bill Summary",
        { ...state.stormSewersExtraItems }
      );

      addLiveDomainSheet(
        "Ponds & Swales",
        liveSummary.pondsSwales,
        "Ponds & Swales Bill Summary",
        { ...state.pondsSwalesExtraItems },
        false
      );

      addLiveDomainSheet(
        "Cellular Attenuation",
        liveSummary.cellularAttenuation,
        "Cellular Attenuation Bill Summary",
        { ...state.cellularAttenuationExtraItems },
        false
      );

      addLiveDomainSheet(
        "Weholite Attenuation",
        liveSummary.weholiteAttenuation,
        "Weholite Attenuation Bill Summary",
        { ...state.weholiteAttenuationExtraItems },
        false
      );

      addLiveDomainSheet(
        "Conc Attenuation",
        liveSummary.concAttenuation,
        "Conc Attenuation Bill Summary",
        { ...state.concAttenuationExtraItems },
        false
      );

      addLiveDomainSheet(
        "Box Culverts",
        liveSummary.boxCulverts,
        "Box Culverts Bill Summary",
        { ...state.boxCulvertsExtraItems },
        false
      );

      addLiveDomainSheet(
        "Roads",
        liveSummary.roads,
        "Roads Bill Summary",
        state.roadsExtraItems ?? {},
        false
      );

      addLiveDomainSheet(
        "Main Services",
        liveSummary.mainServices,
        "Main Services Bill Summary",
        state.mainServicesExtraItems ?? {}
      );

      // Substation, POS, and Compound — on-screen bill-summary sub-tabs that the
      // previous mock-data export omitted entirely.
      addLiveDomainSheet(
        "Substation",
        liveSummary.substation,
        "Substation Bill Summary",
        state.substationExtraItems ?? {}
      );

      addLiveDomainSheet(
        "POS",
        liveSummary.pos,
        "POS Bill Summary",
        state.posExtraItems ?? {}
      );

      addLiveDomainSheet(
        "Compound",
        liveSummary.compound,
        "Compound Bill Summary",
        state.compoundExtraItems ?? {}
      );

      // Dynamic Bill Summary tabs (the "+ Add Tab" bill summaries on this
      // page, see useDynamicBillSummary.tsx) — previously omitted from this
      // export entirely; only each tab's TOTAL reached the Sum Analysis sheet
      // above (rsExportTotals.dynamicBillSummaryTabTotals). One worksheet per
      // tab, mirroring GroundworksReview.tsx's own per-tab
      // createDynamicBillSummarySheet, but built by converting the tab's node
      // tree into addLiveDomainSheet's existing BoqSummaryLineItem walker
      // (via dynamicNodeToBoqLineItem) instead of a second bespoke writer.
      // Created last, same as on screen — the "+ Add Tab" tabs always render
      // after every fixed tab in the tab bar.
      const dynamicTabs = state.roadsAndSewersDynamicBillSummaryTabs ?? [];
      if (dynamicTabs.length > 0) {
        const usedSheetNames = new Set<string>(
          workbook.worksheets.map((ws: any) => String(ws.name).toLowerCase())
        );
        dynamicTabs.forEach((tab) => {
          const sheetName = sanitizeWorksheetName(tab.label, usedSheetNames);
          addLiveDomainSheet(sheetName, [], tab.label, { __root__: tab.nodes });
        });
      }

      // End Cover sheet, appended last — matches groundworks appending one
      // unconditionally to every export.
      await createEndCover(workbook as any, coverSheetData);

      // Export the file
      console.log("📤 Writing Excel file...");
      const buffer = await workbook.xlsx.writeBuffer();

      // Create blob and download
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const timestamp = new Date().toISOString().slice(0, 10);
      const filename = `Roads_Sewers_BOQ_${projectName}_${timestamp}.xlsx`;

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      console.log("✅ Excel file downloaded successfully");

      notification.success({
        message: "Export Complete",
        description: `Roads & Sewers BOQ exported successfully as ${filename}`,
        duration: 3,
      });
    } catch (error) {
      console.error("❌ Excel export error:", error);
      notification.error({
        message: "Export Failed",
        description: "Failed to export to Excel. Please try again.",
        duration: 5,
      });
    }
  };

  // Handle export with options
  const handleExportWithOptions = async (
    options: ExportDetailOptions,
    format: "excel" | "pdf"
  ) => {
    if (format === "excel") {
      // Use comprehensive Excel export with all summary sheets — markup toggles
      // are read reactively from rsExportTotals inside the export itself.
      await handleExportRoadsSewersToExcel(options);
      return;
    }

    if (!boqData || !boqData.items) {
      notification.error({
        message: "Export Error",
        description:
          "No BOQ data available to export. Please calculate the BOQ first.",
      });
      return;
    }

    try {
      // Set export options in BoQExporter
      BoQExporter.selectExportDetails(options);

      console.log("Export data structure:", boqData);

      // Convert boqData to BoQSection format for export
      const sections: BoQSection[] = [];

      // Process each section from boqData.items
      boqData.items.forEach((sectionData: any, sectionIndex: number) => {
        if (sectionData && sectionData.items && sectionData.items.length > 0) {
          const section: BoQSection = {
            id:
              sectionData.category?.toLowerCase().replace(/\s+/g, "_") ||
              `section_${sectionIndex}`,
            title: sectionData.category || `Section ${sectionIndex + 1}`,
            items: sectionData.items.map((item: any, itemIndex: number) => {
              // Handle different amount formats
              let amount = 0;
              if (typeof item.amount === "number") {
                amount = item.amount;
              } else if (typeof item.amount === "string") {
                // Handle special values
                if (
                  item.amount === "Stage 2 Works" ||
                  item.amount === "Excluded" ||
                  item.amount === "Rate Only" ||
                  item.amount === "n/a"
                ) {
                  amount = 0;
                } else {
                  // Parse numeric amount
                  const cleanAmount = item.amount.replace(/[£,]/g, "");
                  amount = parseFloat(cleanAmount) || 0;
                }
              }

              return {
                id: `${sectionData.category}_${itemIndex}`,
                itemNumber: item.item?.toString() || `${itemIndex + 1}`,
                description: item.description || "",
                quantity:
                  typeof item.quantity === "number"
                    ? item.quantity
                    : parseFloat(item.quantity) || 0,
                unit: item.unit || "",
                rate:
                  typeof item.rate === "number"
                    ? item.rate
                    : parseFloat(item.rate) || 0,
                amount: amount,
                level: item.isDiameterHeader ? 1 : 2,
                isHeader: item.isDiameterHeader || false,
                isSubtotal: false,
                isTotal: false,
                notes: item.notes || "",
                status: item.isSpecialValue ? item.amount : undefined,
              };
            }),
          };
          sections.push(section);
        }
      });

      console.log("Converted sections:", sections);

      // Calculate section totals
      const sectionTotals = {
        foulSewers: 0,
        stormSewers: 0,
        roads: 0,
        preliminaryItems: 0,
      };

      boqData.items.forEach((sectionData: any) => {
        const total = sectionData.total || 0;
        switch (sectionData.category) {
          case "FOUL SEWERS":
            sectionTotals.foulSewers = total;
            break;
          case "STORM SEWERS":
            sectionTotals.stormSewers = total;
            break;
          case "ROADS":
            sectionTotals.roads = total;
            break;
          case "MAIN SERVICES":
          case "PRELIMINARY ITEMS":
            sectionTotals.preliminaryItems = total;
            break;
        }
      });

      const summary: BoQSummary = {
        grandTotal: boqData.grandTotal || 0,
        constructionSubtotal: boqData.grandTotal || 0,
        overhead: 0,
        profit: 0,
        labourTotal: 0,
        materialTotal: 0,
        plantTotal: 0,
        sections: sectionTotals,
      };

      const tenderState = {
        projectName:
          boqData.project?.name ||
          state.projectName ||
          "Roads & Sewers Project",
        client: boqData.project?.client || state.client || "Client",
        siteAddress: state.siteAddress || "Site Address",
      };

      console.log("Export summary:", summary);
      console.log("Export tender state:", tenderState);

      if (format === "excel") {
        BoQExporter.exportToExcel(sections, summary, tenderState as any);
        notification.success({
          message: "Export Successful",
          description:
            "Roads & Sewers BOQ has been exported to Excel successfully.",
        });
      } else {
        BoQExporter.exportToPDF(sections, summary, tenderState as any);
        notification.success({
          message: "Export Successful",
          description:
            "Roads & Sewers BOQ has been exported to PDF successfully.",
        });
      }
    } catch (error) {
      notification.error({
        message: "Export Error",
        description: `Failed to export BOQ: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
      });
      console.error("Export error:", error);
      console.error("BOQ Data structure:", boqData);
    }
  };

  // Legacy Export BOQ to Excel (keeping for backward compatibility)
  const handleExportBOQToExcel = () => {
    if (!boqData) {
      notification.error({
        message: "Export Error",
        description: "No BOQ data available to export.",
      });
      return;
    }

    try {
      const excelData = generateBOQExcelData(boqData);
      downloadAsExcel(excelData);
      notification.success({
        message: "Export Successful",
        description: "BOQ has been exported to Excel successfully.",
      });
    } catch (error) {
      notification.error({
        message: "Export Error",
        description: "Failed to export BOQ to Excel. Please try again.",
      });
    }
  };

  const calculationResults = state.calculationResults;
  const hasResults = Boolean(
    calculationResults && calculationResults.isCalculated
  );

  console.log("Review state:", {
    hasResults,
    calculationResults,
    boqData,
    showBoqSection: hasResults && boqData,
  });

  // Comprehensive calculation function that pulls data from all view rates modals
  const calculateComprehensiveBOQ = () => {
    const boqItems: any[] = [];
    let grandTotal = 0;

    console.log("State data:", {
      foulSewers: state.foulSewers,
      stormSewers: state.stormSewers,
      roads: state.roads,
      preliminaryItems: state.preliminaryItems,
    });

    // 1. FOUL SEWERS CALCULATIONS - Using mock data from foulSewersBillSummary
    if (state.foulSewers) {
      const foulSewersSection = {
        category: "FOUL SEWERS",
        items: [] as any[],
        total: 0,
        manholeTotal: 0,
        pipeworkTotal: 0,
        lateralTotal: 0,
      };

      // Transform mock data to BOQ format and filter out items with no quantities
      let itemCounter = 1;
      let sectionTotal = 0;

      // Process foulSewersBillSummary data - only include items with quantities
      foulSewersBillSummaryMock.forEach((mockItem) => {
        // Skip empty rows and headers without quantities
        if (
          !mockItem.Description ||
          mockItem.Description.trim() === "" ||
          !mockItem.Qty ||
          mockItem.Qty.toString().trim() === "" ||
          mockItem.Qty === "0"
        ) {
          // Add section headers for display structure
          if (
            mockItem.Item &&
            (mockItem.Item === "1.0" ||
              mockItem.Item === "2.0" ||
              mockItem.Item === "3.0" ||
              mockItem.Item === "4.0")
          ) {
            foulSewersSection.items.push({
              description: mockItem.Description,
              quantity: "",
              unit: "",
              rate: "",
              amount: "",
              isDiameterHeader: true,
              category:
                mockItem.Item === "1.0"
                  ? "adopted_sewers"
                  : mockItem.Item === "2.0"
                  ? "laterals"
                  : mockItem.Item === "3.0"
                  ? "manholes"
                  : "demarcation",
            });
          }
          return;
        }

        // Parse amount from string (remove £ and commas)
        let amount = 0;
        if (
          mockItem.Amount &&
          mockItem.Amount !== "Rate Only" &&
          mockItem.Amount !== "n/a"
        ) {
          const amountStr = mockItem.Amount.toString().replace(/£|,/g, "");
          amount = parseFloat(amountStr) || 0;
        }

        // Parse rate from string
        let rate = 0;
        if (mockItem.Rate && mockItem.Rate !== "n/a" && mockItem.Rate !== "") {
          rate = parseFloat(mockItem.Rate.toString()) || 0;
        }

        // Parse quantity
        const quantity = parseFloat(mockItem.Qty.toString()) || 0;

        // Only add items with quantities > 0
        if (quantity > 0) {
          foulSewersSection.items.push({
            item: itemCounter++,
            description: mockItem.Description,
            quantity: quantity,
            unit: mockItem.Unit || "",
            rate: rate,
            amount: amount,
            category: "foul_sewers",
          });

          sectionTotal += amount;
        }
      });

      foulSewersSection.total = sectionTotal;
      console.log("Foul sewers section (from mock data):", foulSewersSection);
      boqItems.push(foulSewersSection);
      grandTotal += foulSewersSection.total;
    }

    // 2. STORM SEWERS CALCULATIONS - Using mock data from stormSewersBillSummary
    if (state.stormSewers) {
      const stormSewersSection = {
        category: "STORM SEWERS",
        items: [] as any[],
        total: 0,
        manholeTotal: 0,
        pipeworkTotal: 0,
        lateralTotal: 0,
        gulliesTotal: 0,
      };

      // Transform mock data to BOQ format and filter out items with no quantities
      let itemCounter = 1;
      let sectionTotal = 0;

      // Process stormSewersBillSummary data - only include items with quantities
      stormSewersBillSummaryMock.forEach((mockItem) => {
        // Skip empty rows and headers without quantities
        if (
          !mockItem.Description ||
          mockItem.Description.trim() === "" ||
          !mockItem.Qty ||
          mockItem.Qty.toString().trim() === "" ||
          mockItem.Qty === "0"
        ) {
          // Add section headers for display structure
          if (
            mockItem.Item &&
            (mockItem.Item === "1.0" ||
              mockItem.Item === "2.0" ||
              mockItem.Item === "3.0")
          ) {
            stormSewersSection.items.push({
              description: mockItem.Description,
              quantity: "",
              unit: "",
              rate: "",
              amount: "",
              isDiameterHeader: true,
              category:
                mockItem.Item === "1.0"
                  ? "adopted_sewers"
                  : mockItem.Item === "2.0"
                  ? "laterals"
                  : mockItem.Item === "3.0"
                  ? "gullies"
                  : "storm_sewers",
            });
          }
          return;
        }

        // Parse amount from string (remove £ and commas)
        let amount = 0;
        if (
          mockItem.Amount &&
          mockItem.Amount !== "Rate Only" &&
          mockItem.Amount !== "n/a"
        ) {
          const amountStr = mockItem.Amount.toString().replace(/£|,/g, "");
          amount = parseFloat(amountStr) || 0;
        }

        // Parse rate from string
        let rate = 0;
        if (mockItem.Rate && mockItem.Rate !== "n/a" && mockItem.Rate !== "") {
          rate = parseFloat(mockItem.Rate.toString()) || 0;
        }

        // Parse quantity
        const quantity = parseFloat(mockItem.Qty.toString()) || 0;

        // Only add items with quantities > 0
        if (quantity > 0) {
          stormSewersSection.items.push({
            item: itemCounter++,
            description: mockItem.Description,
            quantity: quantity,
            unit: mockItem.Unit || "",
            rate: rate,
            amount: amount,
            category: "storm_sewers",
          });

          sectionTotal += amount;
        }
      });

      stormSewersSection.total = sectionTotal;
      console.log("Storm sewers section (from mock data):", stormSewersSection);
      boqItems.push(stormSewersSection);
      grandTotal += stormSewersSection.total;
    }

    // 3. ROADS CALCULATIONS - Using mock data from roadsBillSummary
    if (state.roads) {
      const roadsSection = {
        category: "ROADS",
        items: [] as any[],
        total: 0,
      };

      // Transform mock data to BOQ format and filter out items with no quantities
      let itemCounter = 1;
      let sectionTotal = 0;

      // Process roadsBillSummary data - only include items with quantities
      roadsBillSummaryMock.forEach((mockItem) => {
        // Skip empty rows and headers without quantities
        if (
          !mockItem.Description ||
          mockItem.Description.trim() === "" ||
          !mockItem.Qty ||
          mockItem.Qty.toString().trim() === "" ||
          mockItem.Qty === "0"
        ) {
          // Add section headers for display structure
          if (
            mockItem.Item &&
            (mockItem.Item === "1.0" ||
              mockItem.Item === "2.0" ||
              mockItem.Item === "3.0")
          ) {
            roadsSection.items.push({
              description: mockItem.Description,
              quantity: "",
              unit: "",
              rate: "",
              amount: "",
              isDiameterHeader: true,
              category:
                mockItem.Item === "1.0"
                  ? "carriageway"
                  : mockItem.Item === "2.0"
                  ? "footpaths"
                  : mockItem.Item === "3.0"
                  ? "kerbs_edgings"
                  : "roads",
            });
          }
          return;
        }

        // Parse amount from string (remove £ and commas)
        let amount = 0;
        if (
          mockItem.Amount &&
          mockItem.Amount !== "Rate Only" &&
          mockItem.Amount !== "n/a" &&
          mockItem.Amount !== "Stage 2 Works" &&
          mockItem.Amount !== "Excluded"
        ) {
          const amountStr = mockItem.Amount.toString().replace(/£|,/g, "");
          amount = parseFloat(amountStr) || 0;
        }

        // Parse rate from string
        let rate = 0;
        if (mockItem.Rate && mockItem.Rate !== "n/a" && mockItem.Rate !== "") {
          rate = parseFloat(mockItem.Rate.toString()) || 0;
        }

        // Parse quantity
        const quantity = parseFloat(mockItem.Qty.toString()) || 0;

        // Only add items with quantities > 0
        if (quantity > 0) {
          roadsSection.items.push({
            item: itemCounter++,
            description: mockItem.Description,
            quantity: quantity,
            unit: mockItem.Unit || "",
            rate: rate,
            amount: amount,
            category: "roads",
          });

          sectionTotal += amount;
        }

        // Add items with special values like "Stage 2 Works" for display
        if (
          mockItem.Amount === "Stage 2 Works" ||
          mockItem.Amount === "Excluded"
        ) {
          roadsSection.items.push({
            item: itemCounter++,
            description: mockItem.Description,
            quantity: "",
            unit: mockItem.Unit || "",
            rate: "",
            amount: mockItem.Amount,
            category: "roads",
            isSpecialValue: true,
          });
        }
      });

      roadsSection.total = sectionTotal;
      console.log("Roads section (from mock data):", roadsSection);
      boqItems.push(roadsSection);
      grandTotal += roadsSection.total;
    }

    // 4. MAIN SERVICES CALCULATIONS - Using mock data from mainServicesBillSummary
    if (state.preliminaryItems) {
      const mainServicesSection = {
        category: "MAIN SERVICES",
        items: [] as any[],
        total: 0,
      };

      // Transform mock data to BOQ format and filter out items with no quantities
      let itemCounter = 1;
      let sectionTotal = 0;

      // Process mainServicesBillSummary data - only include items with quantities
      mainServicesBillSummaryMock.forEach((mockItem) => {
        // Skip empty rows and headers without quantities
        if (
          !mockItem.Description ||
          mockItem.Description.trim() === "" ||
          !mockItem.Qty ||
          mockItem.Qty.toString().trim() === "" ||
          mockItem.Qty === "0"
        ) {
          // Add section headers for display structure
          if (mockItem.Item && mockItem.Item === "1.0") {
            mainServicesSection.items.push({
              description: mockItem.Description,
              quantity: "",
              unit: "",
              rate: "",
              amount: "",
              isDiameterHeader: true,
              category: mockItem.Item,
            });
          }
          return;
        }

        // Parse amount from string (remove £ and commas)
        let amount = 0;
        if (
          mockItem.Amount &&
          mockItem.Amount !== "Rate Only" &&
          mockItem.Amount !== "Excluded" &&
          mockItem.Amount !== "n/a"
        ) {
          const amountStr = mockItem.Amount.toString().replace(/£|,/g, "");
          amount = parseFloat(amountStr) || 0;
        }

        // Parse rate from string
        let rate = 0;
        if (mockItem.Rate && mockItem.Rate !== "n/a" && mockItem.Rate !== "") {
          rate = parseFloat(mockItem.Rate.toString()) || 0;
        }

        // Parse quantity
        const quantity = parseFloat(mockItem.Qty.toString()) || 0;

        // Only add items with quantities > 0
        if (quantity > 0) {
          mainServicesSection.items.push({
            item: itemCounter++,
            description: mockItem.Description,
            quantity: quantity,
            unit: mockItem.Unit || "",
            rate: rate,
            amount: amount,
            category: "main_services",
          });

          sectionTotal += amount;
        }
      });

      mainServicesSection.total = sectionTotal;
      console.log(
        "Main services section (from mock data):",
        mainServicesSection
      );
      boqItems.push(mainServicesSection);
      grandTotal += mainServicesSection.total;
    }

    // 5. PRELIMINARY ITEMS CALCULATIONS - Using mock data from preliminariesBillSummary
    if (state.preliminaryItems) {
      const preliminarySection = {
        category: "PRELIMINARY ITEMS",
        items: [] as any[],
        total: 0,
      };

      // Transform mock data to BOQ format and filter out items with no quantities
      let itemCounter = 1;
      let sectionTotal = 0;

      // Process preliminariesBillSummary data - only include items with quantities
      preliminariesBillSummaryMock.forEach((mockItem) => {
        // Skip empty rows and headers without quantities
        if (
          !mockItem.Description ||
          mockItem.Description.trim() === "" ||
          !mockItem.Qty ||
          mockItem.Qty.toString().trim() === "" ||
          mockItem.Qty === "0"
        ) {
          // Add section headers for display structure
          if (
            mockItem.Item &&
            (mockItem.Item === "1.0" ||
              mockItem.Item === "2.0" ||
              mockItem.Item === "2.1" ||
              mockItem.Item === "2.2" ||
              mockItem.Item === "3.0")
          ) {
            preliminarySection.items.push({
              description: mockItem.Description,
              quantity: "",
              unit: "",
              rate: "",
              amount: "",
              isDiameterHeader: true,
              category: mockItem.Item,
            });
          }
          return;
        }

        // Parse amount from string (remove £ and commas)
        let amount = 0;
        if (
          mockItem.Amount &&
          mockItem.Amount !== "Rate Only" &&
          mockItem.Amount !== "Stage 2 Works" &&
          mockItem.Amount !== "n/a"
        ) {
          const amountStr = mockItem.Amount.toString().replace(/£|,/g, "");
          amount = parseFloat(amountStr) || 0;
        }

        // Parse rate from string
        let rate = 0;
        if (mockItem.Rate && mockItem.Rate !== "n/a" && mockItem.Rate !== "") {
          rate = parseFloat(mockItem.Rate.toString()) || 0;
        }

        // Parse quantity
        const quantity = parseFloat(mockItem.Qty.toString()) || 0;

        // Only add items with quantities > 0
        if (quantity > 0) {
          preliminarySection.items.push({
            item: itemCounter++,
            description: mockItem.Description,
            quantity: quantity,
            unit: mockItem.Unit || "",
            rate: rate,
            amount: amount,
            category: "preliminary_items",
          });

          sectionTotal += amount;
        }
      });

      preliminarySection.total = sectionTotal;
      console.log(
        "Preliminary items section (from mock data):",
        preliminarySection
      );
      boqItems.push(preliminarySection);
      grandTotal += preliminarySection.total;
    }

    console.log("Final BOQ Items:", boqItems);
    console.log("Grand Total:", grandTotal);

    return {
      items: boqItems,
      grandTotal: grandTotal,
      calculatedAt: new Date(),
      project: {
        name: state.projectName,
        client: state.client,
        region: state.region,
      },
    };
  };

  // Comprehensive data summary for review
  const dataSummary = {
    foulSewers: {
      manholes: state.foulSewers?.manholes?.table?.length || 0,
      pipeRuns: state.foulSewers?.pipeRuns?.table?.length || 0,
      laterals: state.foulSewers?.lateralConnections?.config?.lateralsCount || 0,
      gullies: state.foulSewers?.lateralConnections?.config?.gulliesCount || 0,
      totalPipeLength:
        state.foulSewers?.pipeRuns?.table?.reduce((sum, run) => sum + run.length, 0) ||
        0,
    },
    stormSewers: {
      manholes: state.stormSewers?.manholes?.table?.length || 0,
      pipeRuns: state.stormSewers?.pipeRuns?.table?.length || 0,
      roadGullies: state.stormSewers?.pipeRuns?.config?.roadGulliesCount || 0,
      totalPipeLength:
        state.stormSewers?.pipeRuns?.table?.reduce(
          (sum, run) => sum + run.length,
          0
        ) || 0,
    },
    roads: {
      roadTypes: state.roads?.roadTypes.table?.length || 0,
      sections: state.roads?.cutFillSections.table?.length || 0,
      totalArea:
        state.roads?.cutFillSections.table?.reduce(
          (sum, section) => sum + section.roadArea,
          0
        ) || 0,
    },
    preliminaryItems: {
      autoCalculated:
        state.preliminaryItems?.autoCalculated?.filter((item) => item.included)
          .length || 0,
      manualItems: state.preliminaryItems?.manualItems?.length || 0,
      mainServices: state.preliminaryItems?.mainServices?.length || 0,
      pumpStation: state.preliminaryItems?.pumpStation ? 1 : 0,
      totalValue: state.preliminaryItems?.totals?.grandTotal || 0,
    },
    groundworks: {
      plots: state.groundworks?.plots?.length || 0,
      foundations: state.groundworks?.foundations?.length || 0,
      totalFloorArea:
        state.groundworks?.plots?.reduce(
          (sum, plot) => sum + (plot.floorArea || 0),
          0
        ) || 0,
      privateDrainage: {
        sampledPlots:
          state.groundworks?.privateDrainage?.sampledPlots?.length || 0,
        totalCost: state.groundworks?.privateDrainage?.totals?.grandTotal || 0,
      },
      externalWorks: {
        privateDrives:
          state.groundworks?.externalWorks?.privateDrives?.length || 0,
        sharedDrives:
          state.groundworks?.externalWorks?.sharedDrives?.length || 0,
        gardens: state.groundworks?.externalWorks?.gardens?.length || 0,
        totalCost: state.groundworks?.externalWorks?.totals?.grandTotal || 0,
        topsoilBalance:
          state.groundworks?.externalWorks?.topsoilBalance?.balance || 0,
      },
      additionalItems: {
        retainingWalls:
          state.groundworks?.additionalItems?.retainingWalls?.length || 0,
        attenuationCrates:
          state.groundworks?.additionalItems?.attenuationCrates?.length || 0,
        compoundAreas:
          state.groundworks?.additionalItems?.compoundAreas?.length || 0,
        totalCost: state.groundworks?.additionalItems?.totals?.grandTotal || 0,
      },
      matrix: {
        hasMatrix: Boolean(state.groundworks?.matrix?.plotData?.length),
        plotDataCount: state.groundworks?.matrix?.plotData?.length || 0,
        totalValue:
          state.groundworks?.matrix?.projectTotals?.adjustedTotal || 0,
      },
      totalValue: state.groundworks?.totals?.grandTotal || 0,
    },
  };

  const handleCalculateAll = async () => {
    const ohpPercent = (state.globalSettings as any)?.groundworks?.overheadAndProfit || 0;
    const prelimsPercent = (state.globalSettings as any)?.groundworks?.preliminaries || 0;
    saveBothWallsBuildUp({ tender: state, itemLibraryData, ohpPercent, prelimsPercent });
    setIsCalculating(true);
    setCalculationProgress(null);

    // Re-fetch item library to ensure latest site-specific rates are used —
    // same as GroundworksReview.tsx's own handleCalculateAll.
    await refetchItemLibrary();

    // Wait before proceeding with calculations
    await new Promise((resolve) => setTimeout(resolve, 100));

    try {
      // Generate comprehensive BOQ
      console.log("Starting BOQ calculation...");
      const boqResult = calculateComprehensiveBOQ();
      console.log("BOQ Result:", boqResult);
      setBoqData(boqResult);

      console.log("GrandTotal", calculateSumAnalysisGrandTotal());
      // Generate JSON in new format
      const roadsAndSewersTotal = boqResult.grandTotal;

      // Calculate groundworks total from mock data if groundworks is enabled
      let groundworksTotal = 0;
      if (state.jobTypes.groundworks) {
        try {
          // Import and calculate groundworks total from the same mock data used in GroundworksReview
          const { billOfQuantities } = await import("../../mock/index");
          const billItems = billOfQuantities.map((mockItem) => {
            const nett =
              parseFloat(
                (mockItem.Nett?.toString() || "0").replace(/[£,]/g, "")
              ) || 0;
            const ohp =
              parseFloat(
                (mockItem["OH&P"]?.toString() || "0").replace(/[£,]/g, "")
              ) || 0;
            return { nett, ohp, total: nett + ohp };
          });

          groundworksTotal = billItems.reduce(
            (sum, item) => sum + (item.total || 0),
            0
          );
          console.log("Calculated groundworks total:", groundworksTotal);
        } catch (error) {
          console.error("Error calculating groundworks total:", error);
          groundworksTotal = 0; // Fallback to 0 if calculation fails
        }
      }

      const jsonData = {
        project: {
          name: state.projectName || "Unnamed Project",
          client: state.client || "Unknown Client",
          region: state.region || "Unknown Region",
        },
        calculatedAt: boqResult.calculatedAt,
        billOfQuantities: [
          {
            roadAndSewers: {
              totals: {
                grandTotal: calculateRoadsAndSewersGrandTotal(),
              },
              ohp: globalSettings?.roadsAndSewers?.overheadAndProfit || 0,
              preliminaries: globalSettings?.roadsAndSewers?.preliminaries || 0,
            },
            groundworks: {
              totals: {
                grandTotal: calculateGroundworksGrandTotal(),
              },
              ohp: globalSettings?.groundworks?.overheadAndProfit || 0,
              preliminaries: globalSettings?.groundworks?.preliminaries || 0,
            },
          },
        ],
        totals: {
          tenderTotal: calculateSumAnalysisGrandTotal(),
        },
      };

      console.log(
        "Final totals - Roads & Sewers:",
        calculateRoadsAndSewersGrandTotal(),
        "Groundworks:",
        calculateGroundworksGrandTotal(),
        "Tender Total:",
        calculateSumAnalysisGrandTotal()
      );
      setBoqJson(JSON.stringify(jsonData, null, 2));

      // BOQ data is now displayed on the page automatically when hasResults is true

      // Update traditional calculation results for compatibility
      const results = {
        isCalculated: true,
        validationErrors: [],
        calculatedAt: new Date(),
        region: state.region,
        projectTotals: {
          grandTotal: boqResult.grandTotal,
          foulSewersCost:
            boqResult.items.find((item) => item.category === "FOUL SEWERS")
              ?.total || 0,
          stormSewersCost:
            boqResult.items.find((item) => item.category === "STORM SEWERS")
              ?.total || 0,
          roadsCost:
            boqResult.items.find((item) => item.category === "ROADS")?.total ||
            0,
          preliminaryItemsCost:
            boqResult.items.find(
              (item) => item.category === "PRELIMINARY ITEMS"
            )?.total || 0,
          mainServicesCost:
            boqResult.items.find((item) => item.category === "MAIN SERVICES")
              ?.total || 0,
          subtotal: boqResult.grandTotal * 0.85,
          overhead: boqResult.grandTotal * 0.15 * 0.15,
          profit: boqResult.grandTotal * 0.08,
          breakdown: {
            labour: boqResult.grandTotal * 0.4,
            material: boqResult.grandTotal * 0.35,
            plant: boqResult.grandTotal * 0.25,
          },
        },
      };

      console.log("Updating calculation results:", results);
      updateField("calculationResults", results);
      setHasCalculated(true);
      // Trigger sum analysis recalculation
      setRecalculateTrigger((prev) => prev + 1);
      // Register change for change detection
      registerPageChanges("roads-sewers-review", true, 1);

    } catch (error) {
      console.error("Calculation failed:", error);
    } finally {
      setIsCalculating(false);
      setCalculationProgress(null);
    }
  };

  // Comprehensive validation for submission
  const validateForSubmission = (): {
    isValid: boolean;
    errors: string[];
    warnings: string[];
  } => {
    const errors: string[] = [];
    const warnings: string[] = [];

    // Basic project information
    if (!state.projectName.trim()) {
      errors.push("Project name is required");
    }
    if (!state.client.trim()) {
      errors.push("Client name is required");
    }
    if (!state.siteAddress.trim()) {
      warnings.push("Site address not specified");
    }

    // Job type validation
    if (!state.jobTypes.roadsAndSewers && !state.jobTypes.groundworks) {
      errors.push("At least one job type must be selected");
    }

    // Roads & Sewers validation
    if (state.jobTypes.roadsAndSewers) {
      if (
        dataSummary.foulSewers.pipeRuns === 0 &&
        dataSummary.stormSewers.pipeRuns === 0
      ) {
        errors.push("Roads & Sewers selected but no pipe runs defined");
      }
      if (
        dataSummary.foulSewers.pipeRuns > 0 &&
        dataSummary.foulSewers.manholes === 0
      ) {
        errors.push("Foul sewers requires manholes");
      }
      if (
        dataSummary.preliminaryItems.autoCalculated === 0 &&
        dataSummary.preliminaryItems.manualItems === 0
      ) {
        warnings.push("No preliminary items defined");
      }
    }

    // Groundworks validation
    if (state.jobTypes.groundworks) {
      if (dataSummary.groundworks.plots === 0) {
        errors.push("Groundworks selected but no plots defined");
      }
      if (
        dataSummary.groundworks.plots > 0 &&
        dataSummary.groundworks.foundations === 0
      ) {
        warnings.push("Plots defined but no foundation calculations completed");
      }
    }

    // Calculation validation
    if (!hasResults) {
      errors.push("Calculations must be completed before submission");
    }
    if (hasResults && calculationResults?.validationErrors?.length > 0) {
      errors.push("Calculation errors must be resolved");
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings,
    };
  };

  // Save as Draft handler
  const handleSaveAsDraft = async () => {
    setIsSaving(true);

    try {
      // Update status to In-progress
      updateField("status", "In-progress");
      updateField("lastModified", new Date());

      // Simulate save delay
      await new Promise((resolve) => setTimeout(resolve, 1000));

      notification.success({
        message: "Draft Saved",
        description:
          "Your tender has been saved as a draft. You can continue working on it later.",
        duration: 4,
        placement: "topRight",
      });
    } catch (error) {
      notification.error({
        message: "Save Failed",
        description: "Failed to save draft. Please try again.",
        duration: 4,
        placement: "topRight",
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Submit handler
  const handleSubmit = async () => {
    const validation = validateForSubmission();

    if (!validation.isValid) {
      Modal.error({
        title: "Cannot Submit Tender",
        content: (
          <div>
            <p>Please resolve the following issues before submitting:</p>
            <ul className="mt-2 mb-0">
              {validation.errors.map((error, index) => (
                <li key={index} className="text-red-600">
                  {error}
                </li>
              ))}
            </ul>
            {validation.warnings.length > 0 && (
              <div className="mt-3">
                <p className="mb-1">Warnings:</p>
                <ul className="mt-1 mb-0">
                  {validation.warnings.map((warning, index) => (
                    <li key={index} className="text-orange-600">
                      {warning}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ),
        width: 500,
      });
      return;
    }

    // Show warnings if any and confirm submission
    if (validation.warnings.length > 0) {
      Modal.confirm({
        title: "Submit with Warnings?",
        content: (
          <div>
            <p>The following warnings were found:</p>
            <ul className="mt-2 mb-3">
              {validation.warnings.map((warning, index) => (
                <li key={index} className="text-orange-600">
                  {warning}
                </li>
              ))}
            </ul>
            <p>Do you want to proceed with submission?</p>
          </div>
        ),
        onOk: () => performSubmission(),
        okText: "Submit Anyway",
        cancelText: "Cancel",
      });
    } else {
      setSubmitModalVisible(true);
    }
  };

  // Handler for sending for review
  const handleSendForReview = () => {
    // Snapshot the current modal content so it remains stable while the async
    // submission runs and reviewButtonState recomputes to empty-string values.
    setFrozenReviewModal({
      title: reviewButtonState.modalTitle,
      message: reviewButtonState.modalMessage,
      buttonText: reviewButtonState.buttonText,
      warningMessage:
        (state.roadsAndSewersTotals?.grandTotal || 0) +
          (state.groundworks?.totals?.grandTotal || 0) <
        0
          ? "Warning: The tender total is negative. Please review before submitting."
          : undefined,
    });
    setIsReviewModalVisible(true);
  };

  // Perform the send for review action
  const performSendForReview = async () => {
    setIsSendingForReview(true);

    try {
      // A Roads & Sewers Sum Analysis grand total of 0 means there's
      // effectively no Run Matrix data to submit — same guard as
      // GroundworksReview.tsx's plot-matrix-empty check, worded for this
      // workflow's Run Matrix instead.
      if ((state.roadsAndSewersTotals?.grandTotal || 0) === 0) {
        notification.warning({
          message: "No Data",
          description: "No Run Matrix data available to submit for review.",
          duration: 4,
          placement: "topRight",
        });
        return;
      }

      // Get the next status from review button state
      const nextStatus = reviewButtonState.nextStatus || "In-progress";

      console.log("🚀 ReviewCalculate - Performing send for review:");
      console.log("   Current status:", apiBoqStatus || state.boqStatus);
      console.log("   User role:", userRole);
      console.log("   Next status:", nextStatus);
      console.log("   Button text:", reviewButtonState.buttonText);

      // Update BOQ status and last modified date
      updateField("boqStatus", nextStatus);
      updateField("lastModified", new Date());

      // Groundworks total: whatever's already persisted on state — not
      // recalculated here. createBoqPayload reads the Roads & Sewers total
      // straight off state.roadsAndSewersTotals (the live Sum Analysis
      // TENDER TOTAL kept in sync by useRoadsAndSewersTotalSync) itself.
      const groundworksGrandTotal = state.groundworks?.totals?.grandTotal || 0;

      // Built via the same shared createBoqPayload() GroundworksReview.tsx's own
      // performSendForReview uses — NOT a hand-rolled object — so this includes
      // billOfQuantities[0].roadAndSewers.summary / .groundworks.summary (each
      // domain's computed Bill Summary line items). The previous hand-rolled
      // payload here only ever set totals/ohp/preliminaries, silently omitting
      // `summary` from both the /billofquantities and /boq-versions requests.
      const completePayload = createBoqPayload({
        state,
        userName: user?.name || user?.email,
        status: nextStatus,
        boqStatus: nextStatus,
        opportunityNo: matchedOpportunityNo,
        groundworksTotals: {
          grandTotal: groundworksGrandTotal,
        },
        settings: {
          groundworks: {
            overheadAndProfit: globalSettings?.groundworks?.overheadAndProfit || 0,
            preliminaries: globalSettings?.groundworks?.preliminaries || 0,
            discount: globalSettings?.groundworks?.discount || 0,
          },
        },
        itemLibraryData,
      });

      // Submit to API with new BOQ status (status changed - create version)
      console.log(
        "📤 Submitting tender for review with BOQ status:",
        nextStatus
      );
      console.log("📤 Complete payload:", completePayload);
      await createBoqMutation.mutateAsync(
        createMutationPayload({ boqPayload: completePayload, statusChanged: true }),
      );

      // Create updated state with new BOQ status
      const updatedState = {
        ...state,
        boqStatus: nextStatus,
        status: nextStatus,
        lastModified: new Date(),
      };

      // Dispatch to update React state immediately
      dispatch({
        type: "UPDATE_FIELD",
        payload: { field: "boqStatus", value: nextStatus },
      });
      dispatch({
        type: "UPDATE_FIELD",
        payload: { field: "status", value: nextStatus },
      });

      // Save updated state to localStorage immediately
      saveTenderToStorage(updatedState);

      console.log("✅ State updated with BOQ status:", nextStatus);

      setIsSentForReview(true);

      // Invalidate BOQ cache so returning to this page fetches fresh status.
      queryClient.invalidateQueries({ queryKey: BOQ_QUERY_KEYS.all });
      queryClient.invalidateQueries({ queryKey: ["boq-versions"] });
      queryClient.invalidateQueries({ queryKey: ["boq-version"] });

      // Navigate immediately so updated footer buttons never flash before redirect.
      navigate("/opportunities");

      notification.success({
        message: "Submitted Successfully",
        description: `Your tender has been successfully submitted with status: ${nextStatus}.`,
        duration: 4,
        placement: "topRight",
      });
    } catch (error) {
      console.error("Send for review failed:", error);
      notification.error({
        message: "Send Failed",
        description: "Failed to send tender for review. Please try again.",
        duration: 4,
        placement: "topRight",
      });
    } finally {
      setIsSendingForReview(false);
      setIsReviewModalVisible(false);
      setFrozenReviewModal(null);
    }
  };

  // Handler for changing to in-progress
  const handleChangeToInProgress = () => {
    setIsInProgressModalVisible(true);
  };

  // Perform the change to in-progress action
  const performChangeToInProgress = async () => {
    setIsSendingToInProgress(true);

    try {
      const nextStatus = "In-Progress";

      // Update BOQ status and last modified date
      updateField("boqStatus", nextStatus);
      updateField("lastModified", new Date());

      // Groundworks total: whatever's already persisted on state — not
      // recalculated here. createBoqPayload reads the Roads & Sewers total
      // straight off state.roadsAndSewersTotals (the live Sum Analysis
      // TENDER TOTAL kept in sync by useRoadsAndSewersTotalSync) itself.
      const groundworksGrandTotal = state.groundworks?.totals?.grandTotal || 0;

      // Built via the same shared createBoqPayload() GroundworksReview.tsx's own
      // performChangeToInProgress uses — NOT a hand-rolled object — so this
      // includes billOfQuantities[0].roadAndSewers.summary / .groundworks.summary.
      // The previous hand-rolled payload here only ever set totals/ohp/preliminaries,
      // silently omitting `summary` from both the /billofquantities and
      // /boq-versions requests.
      const completePayload = createBoqPayload({
        state,
        userName: user?.name || user?.email,
        status: nextStatus,
        boqStatus: nextStatus,
        opportunityNo: matchedOpportunityNo,
        groundworksTotals: {
          grandTotal: groundworksGrandTotal,
        },
        settings: {
          groundworks: {
            overheadAndProfit: globalSettings?.groundworks?.overheadAndProfit || 0,
            preliminaries: globalSettings?.groundworks?.preliminaries || 0,
            discount: globalSettings?.groundworks?.discount || 0,
          },
        },
        itemLibraryData,
      });

      // statusChanged: false — the hook's own statusChanged:true path would
      // overwrite the *existing* version's content in place with the new
      // status, corrupting the previous version's history. Version
      // creation/update is handled explicitly below instead (same split as
      // groundworks/review), so the previous version keeps its own status.
      await createBoqMutation.mutateAsync(
        createMutationPayload({ boqPayload: completePayload, statusChanged: false }),
      );

      // Check if we're going from a review status to "In-progress" —
      // same check as groundworks/review, since coming from a review status
      // means a new BOQ version, not an update of the current one.
      const currentStatus = apiBoqStatus || state.boqStatus;
      const reviewStatuses = [
        "Under Review - Senior Estimator",
        "Under Review Commercial Director",
        "Under Client Review",
      ];
      const isReviewToInProgress = reviewStatuses.includes(currentStatus || "");

      if (isReviewToInProgress) {
        // CREATE NEW VERSION: Review status → In-progress
        const currentVersionInfo = await getCurrentBoqVersion(state.projectName, matchedOpportunityNo);
        const nextVersionNumber = currentVersionInfo ? currentVersionInfo.version + 1 : 1;

        const versionPayload = createVersionPayload({
          projectName: state.projectName,
          boqPayload: completePayload,
          changeLog: `Status changed from ${currentStatus} to ${nextStatus}`,
          version: nextVersionNumber,
        });

        await billOfQuantitiesApi.createVersion(versionPayload as any, matchedOpportunityNo);
      } else {
        // UPDATE EXISTING VERSION (or create the first one)
        try {
          const versionInfo = await getCurrentBoqVersion(state.projectName, matchedOpportunityNo);
          if (versionInfo) {
            const versionPayload = createVersionPayload({
              projectName: state.projectName,
              boqPayload: completePayload,
              changeLog: `Status changed to ${nextStatus}`,
              version: versionInfo.version,
            });
            await billOfQuantitiesApi.createVersion(versionPayload as any, matchedOpportunityNo);
          } else {
            const versionPayload = createVersionPayload({
              projectName: state.projectName,
              boqPayload: completePayload,
              changeLog: `Initial version - Status: ${nextStatus}`,
              version: 1,
            });
            await billOfQuantitiesApi.createVersion(versionPayload as any, matchedOpportunityNo);
          }
        } catch (error) {
          // Don't block the flow if version operation fails
        }
      }

      // Create updated state with new BOQ status
      const updatedState = {
        ...state,
        boqStatus: nextStatus,
        status: nextStatus,
        lastModified: new Date(),
      };

      dispatch({
        type: "UPDATE_FIELD",
        payload: { field: "boqStatus", value: nextStatus },
      });
      dispatch({
        type: "UPDATE_FIELD",
        payload: { field: "status", value: nextStatus },
      });

      saveTenderToStorage(updatedState);

      // Invalidate BOQ cache so returning to this page fetches fresh status.
      queryClient.invalidateQueries({ queryKey: BOQ_QUERY_KEYS.all });
      queryClient.invalidateQueries({ queryKey: ["boq-versions"] });
      queryClient.invalidateQueries({ queryKey: ["boq-version"] });

      // Navigate immediately so updated footer buttons never flash before redirect.
      navigate("/opportunities");

      notification.success({
        message: "Status Changed to In-Progress",
        duration: 5,
        description: `Your tender has been successfully changed to status: ${nextStatus}.`,
        placement: "topRight",
      });
    } catch (error) {
      notification.error({
        message: "Failed to Change Status",
        description:
          error instanceof Error
            ? error.message
            : "An error occurred while changing status to in-progress",
        duration: 5,
        placement: "topRight",
      });
    } finally {
      setIsSendingToInProgress(false);
      setIsInProgressModalVisible(false);
    }
  };

  // Handler for marking as won
  const handleMarkAsWon = () => {
    const matched = opportunitiesData?.find(
      (opp) => opp.tender === state.projectName
    );
    if (!matched && !matchedOpportunityNo) {
      setIsManualTenderWarnModalVisible(true);
    } else {
      setIsWonModalVisible(true);
    }
  };

  // Perform the mark as won action
  const performMarkAsWon = async () => {
    setIsSendingToWon(true);

    try {
      const nextStatus = "Won";

      // Update BOQ status and last modified date
      updateField("boqStatus", nextStatus);
      updateField("lastModified", new Date());

      // Roads & Sewers total: the live Sum Analysis TENDER TOTAL. Groundworks
      // total: whatever's already persisted on state — not recalculated.
      // Kept here (separately from createBoqPayload's own internal total)
      // since it's also needed below for the BC opportunity close call's
      // salesLCY field.
      const roadsAndSewersGrandTotal = state.roadsAndSewersTotals?.grandTotal || 0;
      const groundworksGrandTotal = state.groundworks?.totals?.grandTotal || 0;
      const tenderTotal = roadsAndSewersGrandTotal + groundworksGrandTotal;

      // Built via the same shared createBoqPayload() GroundworksReview.tsx's own
      // performMarkAsWon-equivalent uses — NOT a hand-rolled object — so this
      // includes billOfQuantities[0].roadAndSewers.summary / .groundworks.summary.
      // The previous hand-rolled payload here only ever set totals/ohp/preliminaries,
      // silently omitting `summary` from both the /billofquantities and
      // /boq-versions requests.
      const completePayload = createBoqPayload({
        state,
        userName: user?.name || user?.email,
        status: nextStatus,
        boqStatus: nextStatus,
        opportunityNo: matchedOpportunityNo,
        groundworksTotals: {
          grandTotal: groundworksGrandTotal,
        },
        settings: {
          groundworks: {
            overheadAndProfit: globalSettings?.groundworks?.overheadAndProfit || 0,
            preliminaries: globalSettings?.groundworks?.preliminaries || 0,
            discount: globalSettings?.groundworks?.discount || 0,
          },
        },
        itemLibraryData,
      });

      await createBoqMutation.mutateAsync(
        createMutationPayload({ boqPayload: completePayload, statusChanged: true }),
      );

      // Update/create the BOQ version — same pattern GroundworksReview.tsx's own
      // performMarkAsWon uses. Previously missing here entirely, so marking a
      // tender Won on this page never called the /boq-versions endpoint at all —
      // no version snapshot (with `summary`) was ever saved for the status
      // change, unlike every other status transition on this page (see
      // performSendToInProgress above, which does call it).
      try {
        const versionInfo = await getCurrentBoqVersion(state.projectName, matchedOpportunityNo);
        if (versionInfo) {
          const versionPayload = createVersionPayload({
            projectName: state.projectName,
            boqPayload: completePayload,
            changeLog: `Status changed to ${nextStatus}`,
            version: versionInfo.version,
          });
          await billOfQuantitiesApi.createVersion(versionPayload as any, matchedOpportunityNo);
        } else {
          const versionPayload = createVersionPayload({
            projectName: state.projectName,
            boqPayload: completePayload,
            changeLog: `Initial version - Status: ${nextStatus}`,
            version: 1,
          });
          await billOfQuantitiesApi.createVersion(versionPayload as any, matchedOpportunityNo);
        }
      } catch (error) {
        // Don't block the flow if version operation fails
      }

      const updatedState = {
        ...state,
        boqStatus: nextStatus,
        status: nextStatus,
        lastModified: new Date(),
      };

      dispatch({
        type: "UPDATE_FIELD",
        payload: { field: "boqStatus", value: nextStatus },
      });
      dispatch({
        type: "UPDATE_FIELD",
        payload: { field: "status", value: nextStatus },
      });

      saveTenderToStorage(updatedState);

      // Close opportunity in BC if this tender is linked to one
      const matchedOpportunity = opportunitiesData?.find(
        (opp) => opp.tender === state.projectName
      );

      if (matchedOpportunity) {
        const closingDate = new Date().toISOString().split("T")[0];
        try {
          await opportunitiesApi.closeOpportunity(matchedOpportunity.tenderId, {
            actionTaken: "WON",
            closeOpportunityCode: "W-WON",
            closingDate,
            salesLCY: tenderTotal,
            cancelOldTasks: false,
          });
        } catch (bcError: any) {
          const rawMessage =
            bcError?.response?.data?.error?.message ||
            bcError?.message ||
            "An error occurred while updating the opportunity in BC.";
          const cleanMessage = rawMessage.replace(/\s*CorrelationId:.*$/i, "").trim();
          notification.error({
            message: "BC Opportunity Update Failed",
            description: cleanMessage,
            duration: 8,
            placement: "topRight",
          });
        }
      }

      // Invalidate BOQ cache so returning to this page fetches fresh status.
      queryClient.invalidateQueries({ queryKey: BOQ_QUERY_KEYS.all });
      queryClient.invalidateQueries({ queryKey: ["boq-versions"] });
      queryClient.invalidateQueries({ queryKey: ["boq-version"] });

      // Navigate immediately so updated footer buttons never flash before redirect.
      navigate("/opportunities");

      notification.success({
        message: "Status Changed to Won",
        duration: 5,
        description: "Your tender has been successfully marked as Won.",
        placement: "topRight",
      });
    } catch (error) {
      notification.error({
        message: "Failed to Mark as Won",
        description:
          error instanceof Error
            ? error.message
            : "An error occurred while marking tender as won",
        duration: 5,
        placement: "topRight",
      });
    } finally {
      setIsSendingToWon(false);
      setIsWonModalVisible(false);
    }
  };

  // Perform the actual submission — mirrors the "Calculate & Review" button's save
  // (StepperNavigation.tsx's handleCalculateReview: updateField("status", ...) →
  // saveTender() → forceSaveBothBoqEndpoints(state, ...)), so Submit Tender persists
  // through the same canonical save pipeline as every other save action, just with
  // status forced to "Submitted" instead of "In-progress".
  const performSubmission = async () => {
    setIsSubmitting(true);
    setSubmitModalVisible(false);
    setSubmissionProgress("Preparing submission...");

    try {
      // Update status and timestamps. `boqStatus` (when set) always takes precedence
      // over `status` in transformToPayload (see storage.ts) — its type only covers the
      // review-escalation workflow ("In-progress"/"Under Review..."), not a final
      // submitted state, so it must be cleared or a stale review status left in context
      // would override "submitted" on this and every subsequent save.
      updateField("status", "submitted");
      updateField("boqStatus", undefined);
      updateField("lastModified", new Date());

      setSubmissionProgress("Saving Bill of Quantities...");
      await saveTender();
      await new Promise((resolve) => setTimeout(resolve, 200));

      // `state` here is still the closure captured at render time — updateField's
      // dispatch hasn't flowed back into this component's `state` variable yet (React
      // only updates it on the next render), so build the payload from an explicit
      // merged copy instead of relying on `state` to already show "submitted".
      // `boqStatus` (when set) always takes precedence over `status` in
      // transformToPayload (see storage.ts) — its type only covers the review-escalation
      // workflow ("In-progress"/"Under Review..."), not a final submitted state, so it
      // must be cleared here or a stale review status would override "submitted".
      const stateToSubmit = {
        ...state,
        status: "submitted" as const,
        boqStatus: undefined,
        lastModified: new Date(),
      };

      setSubmissionProgress("Submitting to API server...");
      await forceSaveBothBoqEndpoints(
        stateToSubmit,
        "Tender submitted",
        undefined,
        user?.name,
        itemLibraryData,
        matchedOpportunityNo,
      );

      // Clear local storage after successful submission
      clearTenderFromStorage(state.id);

      // Finalize submission
      setSubmissionProgress("Finalizing submission...");
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // Show success notification and navigate to dashboard
      notification.success({
        message: "Tender Submitted Successfully",
        description:
          "Your roads & sewers tender has been submitted successfully.",
        duration: 4,
        placement: "topRight",
      });

      // Navigate to dashboard after successful submission
      setTimeout(() => {
        navigate("/opportunities");
      }, 2000); // Wait 2 seconds to show the success message
    } catch (error) {
      console.error("Submission failed:", error);

      // Check if it's an API error
      const isApiError =
        error && typeof error === "object" && "response" in error;

      notification.error({
        message: "Submission Failed",
        description: isApiError
          ? "Failed to submit Bill of Quantities to the server. Please check your connection and try again."
          : "Failed to submit tender. Please try again.",
        duration: 6,
        placement: "topRight",
      });
    } finally {
      setIsSubmitting(false);
      setSubmissionProgress("");
    }
  };

  // "Save" button shown on every Bill Summary tab while the BOQ is In-progress —
  // same behaviour as GroundworksReview.tsx's handleSaveBillSummary. Mirrors the
  // roadsAndSewers branch of StepperNavigation.tsx's handleCalculateReview (minus
  // the navigate-to-review-page step, since this page IS that page): unlike the
  // groundworks branch, there's no adjustments/globalSettings block to write here —
  // useRoadsAndSewersTotalSync already keeps state.roadsAndSewersTotals in sync on
  // every render, and createBoqPayload reads the grand total from there directly.
  // The walls build-up save still uses the groundworks OH&P/Preliminaries settings
  // (as handleCalculateAll above does) since retaining walls belong to the
  // groundworks side of a combined-job-type tender regardless of which review page
  // triggered the save.
  const [isSavingBillSummary, setIsSavingBillSummary] = useState(false);
  const handleSaveBillSummary = async () => {
    setIsSavingBillSummary(true);
    message.loading({ content: "Saving...", key: "saveBillSummary", duration: 0 });
    try {
      updateField("status", "In-progress");
      updateField("boqStatus", "In-progress");
      updateField("lastModified", new Date());

      await saveTender();
      await new Promise((resolve) => setTimeout(resolve, 200));

      // Same stale-closure guard as handleCalculateReview/performSubmission — build
      // the payload from an explicit merged copy so a stale status doesn't get
      // re-persisted.
      const stateToSave = {
        ...state,
        status: "In-progress" as const,
        boqStatus: "In-progress" as const,
        lastModified: new Date(),
      };

      const { version } = await forceSaveBothBoqEndpoints(
        stateToSave,
        "Save",
        undefined,
        user?.name,
        itemLibraryData,
        matchedOpportunityNo,
      );

      const ohpPercent = globalSettings?.groundworks?.overheadAndProfit || 0;
      const prelimsPercent = globalSettings?.groundworks?.preliminaries || 0;
      await saveBothWallsBuildUp({
        tender: state,
        itemLibraryData,
        ohpPercent,
        prelimsPercent,
        versionOverride: version,
      });

      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: RETAINING_WALLS_BUILD_UP_QUERY_KEYS.detail(state.projectName, version),
        }),
        queryClient.invalidateQueries({
          queryKey: GEO_TOBER_WALL_BUILD_UP_QUERY_KEYS.detail(state.projectName, version),
        }),
      ]);

      message.success({ content: "Saved", key: "saveBillSummary", duration: 2 });
    } catch (error) {
      message.error({ content: "Save failed", key: "saveBillSummary", duration: 3 });
    } finally {
      setIsSavingBillSummary(false);
    }
  };

  const formatCurrency = (amount: number) =>
    `£${amount.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  // Calculate Roads & Sewers grand total (including preliminaries and OH&P)
  const calculateRoadsAndSewersGrandTotal = () => {
    const rsTenderTotalItem = rsSumAnalysis.find(
      (item) => item.Description === "TENDER TOTAL"
    );

    if (!rsTenderTotalItem?.Amount) {
      return 0;
    }

    // Extract the tender total amount (Net amount)
    const tenderTotalAmount = rsTenderTotalItem.Amount || "£0";
    const netAmount = parseFloat(tenderTotalAmount.replace(/[£,]/g, "")) || 0;

    // Calculate OH&P and Preliminaries from net amount
    const preliminariesAmount =
      netAmount * (globalSettings.roadsAndSewers.preliminaries / 100);
    const subtotalAfterPreliminaries = netAmount + preliminariesAmount;
    const ohpAmount =
      subtotalAfterPreliminaries *
      (globalSettings.roadsAndSewers.overheadAndProfit / 100);
    const grandTotal = subtotalAfterPreliminaries + ohpAmount;

    return grandTotal;
  };

  // Calculate Groundworks grand total (including preliminaries and OH&P)
  const calculateGroundworksGrandTotal = () => {
    // Use the same individual item calculation logic as GroundworksReview.tsx
    const applyGlobalSettings = (amount: number) => {
      let adjustedAmount = amount;

      // Apply preliminaries first using groundworks settings
      if (globalSettings.groundworks.preliminaries > 0) {
        adjustedAmount +=
          amount * (globalSettings.groundworks.preliminaries / 100);
      }

      // Apply OH&P to the amount including preliminaries using groundworks settings
      if (globalSettings.groundworks.overheadAndProfit > 0) {
        adjustedAmount +=
          adjustedAmount * (globalSettings.groundworks.overheadAndProfit / 100);
      }

      return adjustedAmount;
    };

    // Calculate total by summing all individual items (same logic as GroundworksReview)
    let calculatedTenderTotal = 0;

    groundworksSumAnalysis.forEach((item: any) => {
      if (
        item.Amount &&
        item.Amount !== "£0" &&
        item.Amount !== "" &&
        !item.Description?.includes("TENDER TOTAL") &&
        !item.Description?.includes("GRAND TOTAL") &&
        !item.Description?.includes("PRELIMINARIES (") &&
        !item.Description?.includes("OVERHEAD & PROFIT (") &&
        item.Description !== ""
      ) {
        // Extract the original amount (handle both positive and negative amounts including discounts)
        const originalAmount =
          parseFloat(item.Amount.replace(/[£,]/g, "")) || 0;

        if (originalAmount !== 0) {
          // Apply global settings to positive amounts, but add negative amounts (discounts) directly
          let adjustedAmount;
          if (originalAmount > 0) {
            adjustedAmount = applyGlobalSettings(originalAmount);
          } else {
            // For discounts (negative amounts), add them directly without applying OH&P or preliminaries
            adjustedAmount = originalAmount;
          }

          calculatedTenderTotal += adjustedAmount;
        }
      }
    });

    return calculatedTenderTotal;
  };

  // Calculate the combined sum analysis grand total (including preliminaries and OH&P)
  const calculateSumAnalysisGrandTotal = () => {
    let combinedTotal = 0;

    // Add Roads & Sewers grand total if selected
    if (state.jobTypes.roadsAndSewers) {
      combinedTotal += calculateRoadsAndSewersGrandTotal();
    }

    // Add Groundworks grand total if selected
    if (state.jobTypes.groundworks) {
      combinedTotal += calculateGroundworksGrandTotal();
    }

    return combinedTotal;
  };

  const getMissingDataWarnings = () => {
    const warnings: string[] = [];

    // Check if any job types are selected
    if (!state.jobTypes.roadsAndSewers && !state.jobTypes.groundworks) {
      warnings.push(
        "No job types selected - please select Roads & Sewers and/or Groundworks"
      );
      return warnings;
    }

    // Roads & Sewers validation
    if (state.jobTypes.roadsAndSewers) {
      if (
        dataSummary.foulSewers.pipeRuns === 0 &&
        dataSummary.stormSewers.pipeRuns === 0 &&
        dataSummary.roads.sections === 0
      ) {
        warnings.push(
          "Roads & Sewers selected but no construction data entered"
        );
      }

      if (
        dataSummary.foulSewers.pipeRuns > 0 &&
        dataSummary.foulSewers.manholes === 0
      ) {
        warnings.push("Foul sewers has pipe runs but no manholes");
      }

      if (
        dataSummary.stormSewers.pipeRuns > 0 &&
        dataSummary.stormSewers.manholes === 0
      ) {
        warnings.push("Storm sewers has pipe runs but no manholes");
      }

      if (dataSummary.roads.sections > 0 && dataSummary.roads.roadTypes === 0) {
        warnings.push("Road sections exist but no road types configured");
      }
    }

    // Groundworks validation
    if (state.jobTypes.groundworks) {
      if (dataSummary.groundworks.plots === 0) {
        warnings.push("Groundworks selected but no plots defined");
      }

      if (
        dataSummary.groundworks.plots > 0 &&
        dataSummary.groundworks.foundations === 0
      ) {
        warnings.push("Plots defined but no foundation calculations completed");
      }

      if (
        dataSummary.groundworks.plots > 0 &&
        !dataSummary.groundworks.matrix.hasMatrix
      ) {
        warnings.push("Plots defined but payment matrix not generated");
      }
    }

    // Cross-module validation
    if (state.jobTypes.roadsAndSewers && state.jobTypes.groundworks) {
      if (
        dataSummary.groundworks.plots > 0 &&
        dataSummary.groundworks.privateDrainage.sampledPlots === 0
      ) {
        warnings.push("Plots defined but no private drainage measured");
      }
    }

    return warnings;
  };

  const missingDataWarnings = getMissingDataWarnings();
  const canCalculate = missingDataWarnings.length === 0;

  // Handler functions for TenderDetailPage navigation
  const handleViewReview = (
    status: "Approved" | "Pending review" | "Submitted"
  ) => {
    setTenderApprovalStatus(status);
    setViewMode("review");
  };

  const handleSubmitTender = () => {
    setSubmitModalVisible(true);
  };

  // Calculate tender amount for display - using Roads & Sewers grand total
  const roadsAndSewersTotal = calculateRoadsAndSewersGrandTotal();
  const tenderAmount =
    roadsAndSewersTotal > 0
      ? `£ ${roadsAndSewersTotal.toLocaleString("en-GB", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`
      : "£ 0.00";

  return (
    <MainLayout canProceed={true} showFooter={false} showStepper={false}>
      {/* Show Tender Detail Page after successful send for review */}
      {viewMode === "detail" && (
        <TenderDetailPage
          onSubmitTender={handleSubmitTender}
          onViewReview={handleViewReview}
          tenderName={state.projectName || "Tender name goes here"}
          tenderAmount={tenderAmount}
          initialStatus={tenderApprovalStatus}
        />
      )}

      {/* Original Review & Calculate view */}
      {viewMode === "review" && (
        <div className="max-w-full mx-auto">
          <Card className="shadow-sm" bodyStyle={{ padding: 0 }}>
            {/* Header */}
            <div
              className="px-6 pt-6 pb-4"
              style={{ borderBottom: "1px solid #e5e7eb" }}
            >
              <div className="flex justify-between items-center">
                <div>
                  <Title
                    level={3}
                    className="mb-1"
                    style={{
                      fontWeight: 600,
                      fontSize: "20px",
                      color: "#111827",
                    }}
                  >
                    Review & Calculate
                  </Title>
                  <Typography.Text
                    type="secondary"
                    style={{ fontSize: "14px", color: "#6b7280" }}
                  >
                    Calculate and review your tender before submission.
                  </Typography.Text>
                </div>
                <Space>
                {showConfigureBoqSwitch && !isHistoricalVersion && (() => {
                  // Computed here (not inside the hasResults-gated IIFE below)
                  // so the "Configure BOQ" switch — which gates controls that
                  // are always visible on the Bill Summary tabs regardless of
                  // whether a calculation has run — isn't itself hidden behind
                  // `hasResults && boqData`. A first-time user on this page who
                  // hasn't clicked "Calculate" yet would otherwise have no way
                  // to ever turn the switch on. Visible to every user, but only
                  // where showConfigureBoqSwitch is on (DOGMA QA).
                  const boqStatus = (state.boqStatus || apiBoqStatus || "In-progress").toLowerCase().trim();
                  const isInProgress = boqStatus === "in-progress";
                  return (
                    isInProgress && (
                      <Space size={8}>
                        <Typography.Text style={{ fontSize: 13, color: "#374151" }}>
                          {CONFIGURE_BOQ_SWITCH_LABEL}
                        </Typography.Text>
                        <Switch
                          checked={configureBoq}
                          onChange={setConfigureBoq}
                          title="When on, lets you add sections/items in every Bill Summary and Dynamic Bill Summary tab. When off, existing sections/items stay visible but the Add Section/Actions controls hide."
                        />
                      </Space>
                    )
                  );
                })()}
                {/* No longer gated on hasResults (persisted calculationResults)
                    — that flag is only ever set by this same button's own
                    handleCalculateAll, so a tender that has never been
                    calculated on this page before (a fresh/reloaded session —
                    calculationResults isn't hydrated from anywhere else) could
                    never show the one control that produces it, a chicken-
                    and-egg dead end matching GroundworksReview.tsx's own
                    equivalent gate. Same role/status disable logic below still
                    applies; only the visibility gate changed. */}
                {!isHistoricalVersion && (() => {
                  const boqStatus = (state.boqStatus || apiBoqStatus || "In-progress").toLowerCase().trim();
                  const isInProgress = boqStatus === "in-progress";
                  const userRoleLower = userRole?.toLowerCase() || "";
                  const canEdit =
                    userRoleLower === "estimator" ||
                    userRoleLower === "senior estimator" ||
                    userRoleLower === "commercial director" ||
                    userRoleLower === "board level" ||
                    userRoleLower === "regional md" ||
                    userRoleLower === "system administrator";
                  const isDisabled = !canEdit || !isInProgress;
                  const disabledReason = !isInProgress
                    ? "BOQ must be In-progress to recalculate"
                    : "Estimator, Senior Estimator, Commercial Director, Board Level, Regional MD, or System Administrator role required to recalculate";
                  return (
                    <Space>
                      <Button
                        type="primary"
                        icon={<CalculatorOutlined />}
                        onClick={handleCalculateAll}
                        loading={isCalculating}
                        disabled={isDisabled}
                        title={isDisabled ? disabledReason : ""}
                        style={{
                          backgroundColor: isDisabled ? "#d9d9d9" : "#1890ff",
                          borderColor: isDisabled ? "#d9d9d9" : "#1890ff",
                        }}
                      >
                        Re-calculate
                      </Button>
                    </Space>
                  );
                })()}
                </Space>
              </div>
            </div>


            {/* Calculation Progress */}
            {calculationProgress && (
              <div className="px-6 py-6">
                <div className="text-center">
                  <div className="mb-4">
                    <Title level={4}>{calculationProgress.stage}</Title>
                    <Progress
                      percent={calculationProgress.progress}
                      status={
                        calculationProgress.progress === 100
                          ? "success"
                          : "active"
                      }
                    />
                  </div>
                  {calculationProgress.details && (
                    <div className="text-gray-600">
                      {calculationProgress.details}
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="px-6 py-6">
              {/* Top Level Tabs - Bill of Quantities Summary and Run Matrix */}
              <div style={{ marginBottom: "16px" }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-end",
                  }}
                >
                  <div style={{ display: "flex", gap: "4px" }}>
                    {[
                      { key: "boq", label: "Bill of Quantities Summary" },
                      { key: "run-matrix", label: "Run Matrix" },
                    ].map(({ key, label }) => (
                      <Button
                        key={key}
                        type={activeMainTab === key ? "primary" : "default"}
                        onClick={() => setActiveMainTab(key)}
                        style={{
                          backgroundColor: activeMainTab === key ? COLORS.primary : "#E1E6EF",
                          borderColor: activeMainTab === key ? COLORS.primary : "#E1E6EF",
                          color: activeMainTab === key ? "#fff" : "#6B7280",
                          fontWeight: 500,
                          borderRadius: "6px 6px 0 0",
                          height: "38px",
                          paddingLeft: "20px",
                          paddingRight: "20px",
                          borderBottom: activeMainTab === key ? "none" : "1px solid #E1E6EF",
                          position: "relative",
                          zIndex: activeMainTab === key ? 2 : 1,
                        }}
                      >
                        {label}
                      </Button>
                    ))}
                  </div>
                  {/* Gated on hasResults (not boqData — handleExportRoadsSewersToExcel
                      builds every sheet from live tender state via
                      computeRoadsAndSewersBoqSummary/rsExportTotals, not boqData,
                      so requiring boqData just to show this button hid Export
                      Options on every remount (route navigation, reload) until
                      the user re-ran Calculate for no reason) and on
                      rsExportTotals.grandTotal > 0, so the button — and the
                      Excel export it opens — isn't reachable on a tender with
                      nothing entered yet, mirroring the same >£0 guard
                      performSendForReview already applies before Send for
                      Review. Hidden outside DOGMA QA via showRnsExports. */}
                  {showRnsExports && activeMainTab === "boq" && rsExportTotals.grandTotal > 0 && (
                    <Button
                      icon={<DownloadOutlined />}
                      onClick={() => setExportOptionsModalVisible(true)}
                      style={{ color: COLORS.primary, marginBottom: "2px" }}
                    >
                      Export Options
                    </Button>
                  )}
                </div>
              </div>

              {/* Bill of Quantities Tab Content */}
              {activeMainTab === "boq" && (
                <div
                  className="mb-6"
                  style={{
                    border: "1px solid #e5e7eb",
                    borderTop: "none",
                    position: "relative",
                    zIndex: 1,
                  }}
                >
                  {/* Bill Summary Sub-Tabs */}
                  <div
                    style={{
                      paddingLeft: "16px",
                      paddingRight: "16px",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
                      {billSummaryTabs.map(({ key, label }) => (
                        <Button
                          key={key}
                          type={activeTab === key ? "primary" : "default"}
                          onClick={() => setActiveTab(key)}
                          style={{
                            backgroundColor: activeTab === key ? COLORS.primary : "#E1E6EF",
                            borderColor: activeTab === key ? COLORS.primary : "#E1E6EF",
                            color: activeTab === key ? "#fff" : "#6B7280",
                            fontWeight: 500,
                            borderRadius: "6px 6px 0 0",
                            height: "38px",
                            paddingLeft: "20px",
                            paddingRight: "20px",
                            borderBottom: activeTab === key ? "none" : "1px solid #E1E6EF",
                            position: "relative",
                            zIndex: activeTab === key ? 2 : 1,
                          }}
                        >
                          {label}
                        </Button>
                      ))}
                      {dynamicBillSummary.tabDescriptors.map((tab) => (
                        <div
                          key={tab.key}
                          className="group"
                          style={{ display: "flex", alignItems: "center", gap: 2 }}
                        >
                          <Button
                            type={activeTab === tab.key ? "primary" : "default"}
                            onClick={() => setActiveTab(tab.key as string)}
                            style={{
                              backgroundColor: activeTab === tab.key ? COLORS.primary : "#E1E6EF",
                              borderColor: activeTab === tab.key ? COLORS.primary : "#E1E6EF",
                              color: activeTab === tab.key ? "#fff" : "#6B7280",
                              fontWeight: 500,
                              borderRadius: "6px 6px 0 0",
                              height: "38px",
                              paddingLeft: "20px",
                              paddingRight: "12px",
                              borderBottom: activeTab === tab.key ? "none" : "1px solid #E1E6EF",
                              position: "relative",
                              zIndex: activeTab === tab.key ? 2 : 1,
                              display: "inline-flex",
                              alignItems: "center",
                            }}
                          >
                            <span>{tab.label}</span>
                            {!state.isViewMode && dynamicBillSummary.enabled && (
                              <>
                                <EditOutlined
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    dynamicBillSummary.openRenameModal(tab.key as string, tab.label as string);
                                  }}
                                  title="Rename tab"
                                  className="inline-block overflow-hidden max-w-0 ml-0 opacity-0 group-hover:max-w-[14px] group-hover:ml-2 group-hover:opacity-100 transition-all duration-200"
                                  style={{ color: activeTab === tab.key ? "#fff" : "#6B7280" }}
                                />
                                <DeleteOutlined
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setDeleteRsTabId(tab.key as string);
                                  }}
                                  title="Delete tab"
                                  className="inline-block overflow-hidden max-w-0 ml-0 opacity-0 group-hover:max-w-[14px] group-hover:ml-2 group-hover:opacity-100 transition-all duration-200"
                                  style={{ color: "#ff4d4f" }}
                                />
                              </>
                            )}
                          </Button>
                        </div>
                      ))}
                      {dynamicBillSummary.enabled && !state.isViewMode && (
                        <Button
                          type="dashed"
                          icon={<PlusOutlined />}
                          onClick={() => dynamicBillSummary.setAddTabModalVisible(true)}
                          title="Add Tab"
                          style={{ height: "38px", width: "38px", padding: 0 }}
                        />
                      )}
                    </div>
                    {/* Roads' own Expand/Collapse All control now lives inside
                        RoadsBillSummary.tsx's own header, next to its "Add
                        Section" button (same convention PrelimsBillSummary.tsx
                        uses) — rendering a second one here would duplicate it. */}
                  </div>

                  <style>
                    {`
                    .bill-summary-table .ant-table {
                      border-radius: 0 !important;
                    }
                    .bill-summary-table .ant-table-container {
                      border-radius: 0 !important;
                    }
                    .bill-summary-table .ant-table-thead > tr > th {
                      background-color: ${COLORS.tableHeaderBg} !important;
                    }
                  `}
                  </style>
                  <div
                    className="bill-summary-table"
                    style={{
                      borderTop: "1px solid #e5e7eb",
                      position: "relative",
                      zIndex: 1,
                    }}
                  >
                    {/* Sum Analysis Tab */}
                    {activeTab === "sum-analysis" && (
                      <div style={{ padding: 0, margin: 0 }}>
                        <RsSumAnalysisBillSummary
                          recalculateTrigger={recalculateTrigger}
                          includeDiscountDisplay={includeDiscountDisplay}
                          onDiscountCheckboxChange={setIncludeDiscountDisplayPersistent}
                          includePreliminariesDisplay={includePreliminariesDisplay}
                          onPreliminariesCheckboxChange={setIncludePreliminariesDisplayPersistent}
                          includeOhpDisplay={includeOhpDisplay}
                          onOhpCheckboxChange={setIncludeOhpDisplayPersistent}
                          boqStatus={currentBoqStatusForDisplay}
                          isHistoricVersion={isHistoricalVersion}
                        />
                      </div>
                    )}
                    {activeTab === "preliminary-items" && (
                      <div style={{ padding: 0, margin: 0 }}>
                        <PrelimsBillSummary />
                      </div>
                    )}
                    {activeTab === "foul-sewers" && (
                      <div style={{ padding: 0, margin: 0 }}>
                        <FoulSewersBillSummary />
                      </div>
                    )}
                    {activeTab === "storm-sewers" && (
                      <div style={{ padding: 0, margin: 0 }}>
                        <StormSewersBillSummary />
                      </div>
                    )}
                    {activeTab === "roads" && (
                      <div style={{ padding: 0, margin: 0 }}>
                        <RoadsBillSummary
                          expandedRowKeys={roadsBillSummaryExpandedKeys}
                          onExpandedRowKeysChange={setRoadsBillSummaryExpandedKeys}
                        />
                      </div>
                    )}
                    {activeTab === "main-services" && (
                      <div style={{ padding: 0, margin: 0 }}>
                        <MainServicesBillSummary />
                      </div>
                    )}
                    {activeTab === "substation" && (
                      <div style={{ padding: 0, margin: 0 }}>
                        <SubstationBillSummary />
                      </div>
                    )}
                    {activeTab === "pos" && (
                      <div style={{ padding: 0, margin: 0 }}>
                        <POSBillSummary />
                      </div>
                    )}
                    {activeTab === "compound" && (
                      <div style={{ padding: 0, margin: 0 }}>
                        <CompoundBillSummary />
                      </div>
                    )}
                    {dynamicBillSummary.tabDescriptors.find((tab) => tab.key === activeTab) && (
                      <div style={{ padding: 0, margin: 0 }}>
                        {dynamicBillSummary.tabDescriptors.find((tab) => tab.key === activeTab)?.children}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Run Matrix Tab Content */}
              {activeMainTab === "run-matrix" && (
                <div
                  className="mb-6"
                  style={{
                    border: "1px solid #e5e7eb",
                    borderTop: "none",
                    position: "relative",
                    zIndex: 1,
                  }}
                >
                  <RunMatrix />
                </div>
              )}

              {/* Calculation Results */}
              {hasResults && (
                <>
                  {/* Validation Errors */}
                  {(calculationResults?.validationErrors?.length || 0) > 0 && (
                    <Alert
                      message={
                        <div>
                          <div className="font-semibold mb-2">
                            Calculation Errors:
                          </div>
                          <ul className="m-0 pl-5">
                            {calculationResults?.validationErrors?.map(
                              (error, index) => (
                                <li key={index}>{error}</li>
                              )
                            )}
                          </ul>
                        </div>
                      }
                      type="error"
                      className="mb-6"
                    />
                  )}
                </>
              )}

              {/* Submission Progress */}
              {isSubmitting && submissionProgress && (
                <div className="mt-6">
                  <Alert
                    message={submissionProgress}
                    description="Please wait while we process your submission..."
                    type="info"
                    showIcon
                    className="mb-4"
                  />
                </div>
              )}
            </div>

            {/* Save — shown in the same bottom footer bar as the Submit/Send-for-review
                buttons, on every Bill Summary tab except Sum Analysis (which already has
                its own Submit/Send-for-review footer below), while the BOQ is In-progress
                AND the "Configure BOQ" switch is on — same gate as the Actions column/Add
                Section/Add Item controls on these tabs (useCanConfigureBoq()), so Save
                doesn't stay reachable once editing has otherwise been turned off.
                Mirrors GroundworksReview.tsx's own Save button. */}
            {activeMainTab === "boq" &&
              activeTab !== "sum-analysis" &&
              !isHistoricalVersion &&
              !state.isViewMode &&
              configureBoq &&
              currentBoqStatusForDisplay.toLowerCase().trim() === "in-progress" && (
                <div style={{ borderTop: "1px solid #e5e7eb" }}>
                  <div className="px-6 py-4 flex justify-end">
                    <Button
                      type="primary"
                      icon={<SaveOutlined />}
                      loading={isSavingBillSummary}
                      onClick={handleSaveBillSummary}
                      style={{ backgroundColor: COLORS.primary, borderColor: COLORS.primary }}
                    >
                      Save
                    </Button>
                  </div>
                </div>
              )}

            {/* Special footer for Commercial Director once status is "Under Client Review":
                the terminal decision point — send back to In-Progress, or mark the tender Won.
                Only shown on Sum Analysis tab (not on other bill-summary tabs or Run Matrix). */}
            {activeMainTab === "boq" &&
              activeTab === "sum-analysis" &&
              !isHistoricalVersion &&
              currentBoqStatusForDisplay.toLowerCase().trim() ===
                "under client review" &&
              userRole?.toLowerCase() === "commercial director" && (
                <div style={{ borderTop: "1px solid #e5e7eb" }}>
                  <div className="px-6 py-4 flex justify-end">
                    <Space>
                      <Button
                        type="default"
                        onClick={handleChangeToInProgress}
                        disabled={isSendingToInProgress || isSendingToWon}
                      >
                        Change to In-Progress
                      </Button>
                      <Button
                        type="primary"
                        icon={<CheckCircleOutlined />}
                        onClick={handleMarkAsWon}
                        disabled={isSendingToWon || isSendingToInProgress}
                        style={{
                          backgroundColor: COLORS.primary,
                          borderColor: COLORS.primary,
                        }}
                      >
                        Mark as Won
                      </Button>
                    </Space>
                  </div>
                </div>
              )}

            {/* Footer with Send for review, Submit Tender, or ReSubmit Tender button.
                Single ternary — same structure as GroundworksReview.tsx's footer —
                so the review-escalation button and the standalone Submit Tender
                button are mutually exclusive, never both shown at once.
                Only shown on Sum Analysis tab (not on other bill-summary tabs or Run Matrix). */}
            {activeMainTab === "boq" &&
              activeTab === "sum-analysis" &&
              !isHistoricalVersion && (
              <div style={{ borderTop: "1px solid #e5e7eb" }}>
                <div className="px-6 py-4 flex justify-end">
                  {tenderApprovalStatus === "Submitted" ? (
                    <Space>
                      {currentBoqStatusForDisplay.toLowerCase().trim() !==
                        "in-progress" && (
                        <Button
                          type="default"
                          onClick={handleChangeToInProgress}
                          disabled={isSendingToInProgress || isSendingToWon}
                        >
                          Change to In-Progress
                        </Button>
                      )}
                      <Button
                        type="primary"
                        icon={<SendOutlined />}
                        onClick={handleSubmitTender}
                        loading={isSubmitting}
                        disabled={
                          isSubmitting ||
                          isSendingToInProgress ||
                          isSendingToWon ||
                          isSendingForReview
                        }
                        style={{
                          backgroundColor: COLORS.primary,
                          borderColor: COLORS.primary,
                        }}
                      >
                        {isSubmitting ? "Submitting..." : "ReSubmit Tender"}
                      </Button>
                    </Space>
                  ) : tenderApprovalStatus === "Approved" ? (
                    <Space>
                      {currentBoqStatusForDisplay.toLowerCase().trim() !==
                        "in-progress" && (
                        <Button
                          type="default"
                          onClick={handleChangeToInProgress}
                          disabled={isSendingToInProgress || isSendingToWon}
                        >
                          Change to In-Progress
                        </Button>
                      )}
                      <Button
                        type="primary"
                        icon={<SendOutlined />}
                        onClick={handleSubmitTender}
                        loading={isSubmitting}
                        disabled={
                          isSubmitting ||
                          isSendingToInProgress ||
                          isSendingToWon ||
                          isSendingForReview
                        }
                        style={{
                          backgroundColor: COLORS.primary,
                          borderColor: COLORS.primary,
                        }}
                      >
                        {isSubmitting ? "Submitting..." : "Submit Tender"}
                      </Button>
                    </Space>
                  ) : reviewButtonState.shouldShow ? (
                    <Space>
                      {currentBoqStatusForDisplay.toLowerCase().trim() !==
                        "in-progress" && (
                        <Button
                          type="default"
                          onClick={handleChangeToInProgress}
                          disabled={isSendingToInProgress || isSendingToWon}
                        >
                          Change to In-Progress
                        </Button>
                      )}
                      <Button
                        type="primary"
                        icon={<SendOutlined />}
                        onClick={handleSendForReview}
                        loading={isSendingForReview}
                        disabled={
                          reviewButtonState.isDisabled ||
                          isSendingForReview ||
                          isSendingToInProgress ||
                          isSendingToWon
                        }
                        style={{
                          backgroundColor: COLORS.primary,
                          borderColor: COLORS.primary,
                        }}
                      >
                        {isSendingForReview
                          ? "Sending..."
                          : reviewButtonState.buttonText}
                      </Button>
                    </Space>
                  ) : null}
                </div>
              </div>
            )}
          </Card>
        </div>
      )}

      {/* Send for Review Confirmation Modal */}
      <SendTenderReviewModal
        visible={isReviewModalVisible}
        onConfirm={performSendForReview}
        onCancel={() => setIsReviewModalVisible(false)}
        loading={isSendingForReview}
        title={frozenReviewModal?.title ?? reviewButtonState.modalTitle}
        message={frozenReviewModal?.message ?? reviewButtonState.modalMessage}
        confirmButtonText={frozenReviewModal?.buttonText ?? reviewButtonState.buttonText}
        warningMessage={frozenReviewModal?.warningMessage}
      />

      {/* Change to In-Progress Confirmation Modal */}
      <SendTenderReviewModal
        visible={isInProgressModalVisible}
        onConfirm={performChangeToInProgress}
        onCancel={() => setIsInProgressModalVisible(false)}
        loading={isSendingToInProgress}
        title="Change to In-Progress"
        message="Are you sure you want to change the status of this tender to In-Progress?"
        confirmButtonText="Change to In-Progress"
      />

      {/* Mark as Won Confirmation Modal */}
      <SendTenderReviewModal
        visible={isWonModalVisible}
        onConfirm={performMarkAsWon}
        onCancel={() => setIsWonModalVisible(false)}
        loading={isSendingToWon}
        title="Mark as Won"
        message="Are you sure you want to mark this tender as Won?"
        confirmButtonText="Mark as Won"
      />

      {/* Manual Tender Warning Modal */}
      <Modal
        title="Manually Created Tender"
        open={isManualTenderWarnModalVisible}
        onOk={() => {
          setIsManualTenderWarnModalVisible(false);
          setIsWonModalVisible(true);
        }}
        onCancel={() => setIsManualTenderWarnModalVisible(false)}
        okText="Proceed"
        cancelText="Cancel"
      >
        <Alert
          type="warning"
          showIcon
          message="This tender was manually created and is not linked to a BC opportunity. No action will be taken on the BC side."
        />
      </Modal>

      {/* Submit Confirmation Modal */}
      <Modal
        title="Submit Roads & Sewers Tender"
        open={submitModalVisible}
        onCancel={() => setSubmitModalVisible(false)}
        footer={
          <div
            className="flex justify-end gap-2"
            style={{
              borderTop: "1px solid #f0f0f0",
              paddingTop: "16px",
              marginLeft: "-24px",
              marginRight: "-24px",
              paddingLeft: "24px",
              paddingRight: "24px",
              marginBottom: "-20px",
              paddingBottom: "20px",
            }}
          >
            <Button
              onClick={() => setSubmitModalVisible(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              icon={<SendOutlined />}
              onClick={performSubmission}
              loading={isSubmitting}
              style={{
                backgroundColor: "#1890ff",
                borderColor: "#1890ff",
              }}
            >
              Confirm & Submit
            </Button>
          </div>
        }
        width={500}
        closable={true}
        centered
        styles={{
          header: {
            backgroundColor: "#f5f5f5",
            marginLeft: "-24px",
            marginRight: "-24px",
            marginTop: "-20px",
            paddingLeft: "24px",
            paddingRight: "24px",
            paddingTop: "16px",
            paddingBottom: "16px",
          },
        }}
      >
        <div className="py-2">
          <p className="text-base mb-3">
            Do you want to submit this roads & sewers tender?
          </p>
          <p className="text-sm text-gray-500 mb-0">
            Once submitted, the tender will be marked as complete and ready for
            client presentation.
          </p>
        </div>
      </Modal>

      {/* Export Options Modal */}
      <ExportOptionsModal
        visible={exportOptionsModalVisible}
        onClose={() => setExportOptionsModalVisible(false)}
        onExport={handleExportWithOptions}
        title="Export Roads & Sewers Bill of Quantities"
      />

      {/* JSON Export Modal */}
      <Modal
        title="View Bill of Quantities JSON"
        open={boqModalVisible}
        onCancel={() => setBoqModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setBoqModalVisible(false)}>
            Close
          </Button>,
          <Button
            key="excel"
            type="primary"
            icon={<DownloadOutlined />}
            onClick={handleExportBOQToExcel}
          >
            Export to Excel
          </Button>,
          <Button
            key="download"
            onClick={() => {
              const blob = new Blob([boqJson], { type: "application/json" });
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url;
              a.download = `${state.projectName || "project"}_BOQ.json`;
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
              URL.revokeObjectURL(url);
            }}
          >
            Download JSON
          </Button>,
        ]}
        width={800}
      >
        <div>
          <Typography.Title level={4} className="mb-4">
            JSON Data View
          </Typography.Title>
          <Typography.Paragraph>
            This JSON contains all the bill of quantities data. Use the "Export
            Options" button above for formatted exports to Excel or PDF.
          </Typography.Paragraph>
          <Input.TextArea
            value={boqJson}
            readOnly
            rows={20}
            className="font-mono text-sm"
          />
        </div>
      </Modal>

      {/* Add a new Dynamic Bill Summary tab — also doubles as the Edit-icon-
          triggered rename modal (dynamicBillSummary.renameTabId set), sharing
          this same markup with handleAddTab instead of a second Modal. */}
      <Modal
        title={dynamicBillSummary.renameTabId ? "Rename Bill Summary" : "Add Bill Summary"}
        open={dynamicBillSummary.addTabModalVisible || dynamicBillSummary.renameTabId !== null}
        onOk={dynamicBillSummary.renameTabId ? dynamicBillSummary.commitRenameModal : dynamicBillSummary.handleAddTab}
        onCancel={() => {
          if (dynamicBillSummary.renameTabId) {
            dynamicBillSummary.closeRenameModal();
          } else {
            dynamicBillSummary.setAddTabModalVisible(false);
            dynamicBillSummary.setNewTabName("");
          }
        }}
        okText={dynamicBillSummary.renameTabId ? "Save" : "Add"}
        okButtonProps={{
          disabled: dynamicBillSummary.renameTabId
            ? !dynamicBillSummary.renameTabLabel.trim() ||
              dynamicBillSummary.isCurrentTabNameDuplicate ||
              dynamicBillSummary.isCurrentTabNameReserved
            : !dynamicBillSummary.newTabName.trim() ||
              dynamicBillSummary.isCurrentTabNameDuplicate ||
              dynamicBillSummary.isCurrentTabNameReserved,
        }}
        destroyOnClose
      >
        <TextInput
          placeholder="Name"
          value={dynamicBillSummary.renameTabId ? dynamicBillSummary.renameTabLabel : dynamicBillSummary.newTabName}
          textOnly
          onChange={(value) =>
            dynamicBillSummary.renameTabId
              ? dynamicBillSummary.setRenameTabLabel(value)
              : dynamicBillSummary.setNewTabName(value)
          }
          onPressEnter={dynamicBillSummary.renameTabId ? dynamicBillSummary.commitRenameModal : dynamicBillSummary.handleAddTab}
          maxLength={50}
          status={
            dynamicBillSummary.isCurrentTabNameDuplicate || dynamicBillSummary.isCurrentTabNameReserved
              ? "error"
              : undefined
          }
          autoFocus
        />
        {dynamicBillSummary.isCurrentTabNameReserved ? (
          <Typography.Text type="danger" style={{ display: "block", marginTop: 4, fontSize: 12 }}>
            This is a default Bill Summary tab name and can't be used here.
          </Typography.Text>
        ) : (
          dynamicBillSummary.isCurrentTabNameDuplicate && (
            <Typography.Text type="danger" style={{ display: "block", marginTop: 4, fontSize: 12 }}>
              A Bill Summary tab with this name already exists.
            </Typography.Text>
          )
        )}
      </Modal>

      <DeleteConfirmationModal
        visible={deleteRsTabId !== null}
        onConfirm={() => {
          if (deleteRsTabId) handleDeleteRsDynamicTab(deleteRsTabId);
          setDeleteRsTabId(null);
        }}
        onCancel={() => setDeleteRsTabId(null)}
        title="Delete this tab and all its contents?"
      />
    </MainLayout>
  );

  function getRegionalMultipliers() {
    const multipliers = {
      "West Midlands": { labour: 1.0, material: 1.0 },
      "East Midlands": { labour: 0.97, material: 0.98 },
      Southern: { labour: 1.1, material: 1.05 },
      Northwest: { labour: 0.93, material: 0.96 },
    };
    return (
      multipliers[state.region as keyof typeof multipliers] || {
        labour: 1.0,
        material: 1.0,
      }
    );
  }
};

export default ReviewCalculate;
